import React, { useState, useRef, useEffect } from 'react';
import { IconAccount, IconChevronRight, IconFilter, IconInsurance, IconSuccess } from '@americanexpress/dls-icons';
import { mycaAuthConfig, resolveMycaLoginUrl, resolveMycaLogoutUrl } from './appConfig';
import { formatByteSize } from './faceImage';
import './verifoneFaceCapture';

// Hardcoded users for demo
const USERS = [{
  id: 'AB1',
  password: 'ab'
}, {
  id: 'AB2',
  password: 'ab'
}];

// Persist per-user enrolled cards in localStorage
const STORAGE_KEY = 'biometric_enrolled_cards';
const MYCA_PENDING_LOGIN_KEY = 'biometric_myca_login_pending';
const MYCA_PENDING_USER_KEY = 'biometric_myca_login_user';
const MYCA_LOGIN_TIMEOUT_MS = 8000;
const DEFAULT_UPDATE_FAILURE_CARD_ID = 'amex-blue';
const CARD_VERIFICATION_STORAGE_KEY = 'biometric_card_verification_attempts';
const CARD_VERIFICATION_LOCK_MS = 24 * 60 * 60 * 1000;
const FACE_VERIFICATION_STATUS_STORAGE_KEY = 'biometric_face_verification_status';
const CARD_IMAGE_URLS = ['https://www.aexp-static.com/online/myca/shared/summary/cardasset/images/NUS000000344_160x101_STRAIGHT_96.jpg', 'https://www.aexp-static.com/online/myca/shared/summary/cardasset/images/NUS000000305_160x101_STRAIGHT_96.jpg', 'https://www.aexp-static.com/online/myca/shared/summary/cardasset/images/NUS000000327_160x101_STRAIGHT_96.jpg'];
const cardImageAt = index => CARD_IMAGE_URLS[index % CARD_IMAGE_URLS.length];
const formatFaceVerificationDate = () => new Date().toLocaleDateString('en-US', {
  month: 'long',
  day: 'numeric',
  year: 'numeric'
});
function VerificationLockNotification({
  autoDismiss = true,
  className,
  message,
  onDismiss
}) {
  const [isVisible, setIsVisible] = useState(true);
  useEffect(() => {
    if (!autoDismiss) return undefined;
    const timer = window.setTimeout(() => {
      setIsVisible(false);
      onDismiss === null || onDismiss === void 0 || onDismiss();
    }, 3000);
    return () => window.clearTimeout(timer);
  }, [autoDismiss, onDismiss]);
  if (!isVisible) return null;
  return /*#__PURE__*/React.createElement("div", {
    className: `face-verification-card-notification face-verification-card-notification-error ${className}`,
    role: "alert"
  }, /*#__PURE__*/React.createElement("span", {
    className: "face-verification-notification-icon face-verification-lock-icon",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none"
  }, /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M9.959 2.27154C10.5355 1.12404 11.9165 0.669539 13.044 1.25654C13.4344 1.46072 13.7583 1.7723 13.9775 2.15454L14.0405 2.27154L23.748 19.603C24.3245 20.7505 23.878 22.157 22.7515 22.744C22.4699 22.8909 22.1605 22.9768 21.8435 22.996L21.7075 23H2.292C1.026 23 0 21.95 0 20.666C-6.98319e-06 20.3435 0.0658373 20.0243 0.1935 19.728L0.252 19.603L9.959 2.27154ZM12 17.0001C11.4477 17.0001 11 17.4478 11 18.0001C11 18.5524 11.4477 19.0001 12 19.0001C12.5523 19.0001 13 18.5524 13 18.0001C13 17.4478 12.5523 17.0001 12 17.0001ZM11.884 8.50621L11.976 8.49971L11.9765 8.49921L12.079 8.50171L12.159 8.51171C12.6105 8.58171 12.961 8.94521 12.997 9.39521L13 9.47121L12.7915 14.7597L12.785 14.8317C12.7335 15.2097 12.4 15.4997 12 15.4997C11.575 15.4997 11.225 15.1727 11.209 14.7597L11.001 9.50871V9.43271C11.02 8.95621 11.396 8.55621 11.884 8.50621Z",
    fill: "#B41601"
  }))), /*#__PURE__*/React.createElement("p", null, message), /*#__PURE__*/React.createElement("button", {
    type: "button",
    "aria-label": "Dismiss notification",
    onClick: () => {
      setIsVisible(false);
      onDismiss === null || onDismiss === void 0 || onDismiss();
    }
  }, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  })));
}
const loadAllEnrollments = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    return {};
  }
};
const saveAllEnrollments = data => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
};
const screens = [{
  key: 'login',
  copy: '',
  buttonLabel: 'Login',
  details: ['App/Link entry point', 'Temporary shell screen', 'Routes to enrollment consent']
}, {
  key: 'consent',
  //eyebrow: 'Biometric POC Enrollment',
  title: 'Review enrollment and consent',
  copy: '',
  buttonLabel: 'Enroll',
  details: ['Show consent summary', 'Confirm member understands the flow', 'Proceed to photo capture']
}, {
  key: 'capture',
  eyebrow: 'Photo Capture',
  title: 'Center your face',
  copy: '',
  buttonLabel: 'Capture Photo',
  details: ['Preview camera frame', 'Guide positioning and lighting', 'Advance without device camera for now']
}, {
  key: 'processing',
  eyebrow: 'Processing',
  title: '',
  copy: '',
  buttonLabel: 'Match found',
  details: ['Processing mock state', 'Route to payment method selection']
}, {
  key: 'payment-selection',
  title: 'Payment method selection',
  copy: 'Choose payment method scenario.',
  buttonLabel: 'Open scenario',
  details: ['Scenario hub for payment method variants']
}, {
  key: 'payment-selection-first-single',
  title: 'Payment method selection',
  copy: 'First-time card member use, single card account.',
  buttonLabel: 'Activate',
  details: ['Review available card', 'Continue to payment processing']
}, {
  key: 'payment-selection-returning-single',
  title: 'Payment method selection',
  copy: 'Returning card member, single card account.',
  buttonLabel: 'Activate',
  details: ['Returning member single card scenario']
}, {
  key: 'payment-selection-first-multi',
  title: 'Payment method selection',
  copy: 'First-time cardmember use, multiple cards, no default card.',
  buttonLabel: 'Activate',
  details: ['Multiple cards without default selection']
}, {
  key: 'ready-to-pay',
  title: 'You’re ready to pay with Face Verification.',
  copy: '',
  buttonLabel: 'Done',
  details: ['Explain how to use Face Verification at checkout']
}, {
  key: 'face-verification-status',
  title: 'Face Verification',
  copy: '',
  buttonLabel: '',
  details: ['Show active Face Verification status', 'Show enrolled cards', 'Allow card unenrollment']
}, {
  key: 'face-verification-cards',
  title: 'Cards',
  copy: '',
  buttonLabel: '',
  details: ['Show cards ready to use with Face Verification', 'Show cards available to add']
}, {
  key: 'adding-card-processing',
  title: 'Adding your card...',
  copy: '',
  buttonLabel: '',
  details: ['Show card provisioning progress after OTP verification']
}, {
  key: 'payment-selection-multi',
  title: 'Payment method selection',
  copy: 'Returning card member, multiple cards, default card set.',
  buttonLabel: 'Activate',
  details: ['Switch selected card', 'Continue to payment processing']
}, {
  key: 'payment-no-eligible',
  title: 'No eligible payment methods',
  copy: 'No eligible cards found for this account.',
  buttonLabel: 'Back to selection',
  details: ['Fallback state when no cards can be used']
}, {
  key: 'payment-processing',
  title: 'Payment processing',
  copy: 'Processing enrollment...',
  buttonLabel: 'Enrollment approved',
  details: ['Mock payment processing state']
}, {
  key: 'payment-approved',
  title: 'Enrollment approved',
  copy: 'Payment complete!',
  buttonLabel: 'Start Over',
  details: ['Terminal approved state']
}, {
  key: 'payment-declined',
  title: 'Enrollment declined',
  copy: 'Enrollment declined.',
  buttonLabel: 'Start Over',
  details: ['Terminal declined state']
}, {
  key: 'match-not-found-scenarios',
  title: 'Match not found scenarios',
  copy: 'Choose a fallback scenario to continue testing.',
  buttonLabel: 'Open scenario',
  details: ['Scenario selector for no-match branch']
}, {
  key: 'verifone-not-enrolled',
  title: 'Amex: Not enrolled with Verifone',
  copy: 'We cannot find you, scan QR code to enroll in Amex app.',
  buttonLabel: 'Back to scenarios',
  details: ['No match found in Verifone']
}, {
  key: 'failed-verification',
  title: 'Amex: Failed verification',
  copy: 'No match found.',
  buttonLabel: 'Back to scenarios',
  details: ['Final failure state']
}, {
  key: 'capture-success',
  eyebrow: 'Capture successful',
  title: 'Photo processing successful',
  copy: 'Your photo is valid and ready for enrollment.',
  buttonLabel: 'Start Over',
  details: ['Restart shell flow']
}];
export default function App() {
  const [stepIndex, setStepIndex] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);
  const [authProvider, setAuthProvider] = useState(null);
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [captureMetrics, setCaptureMetrics] = useState(null);
  const [selectedPaymentCard, setSelectedPaymentCard] = useState('amex-blue');
  const [defaultPaymentCard, setDefaultPaymentCard] = useState('amex-blue');
  const [enrolledCards, setEnrolledCards] = useState([]);
  const [enrollmentConsentChecked, setEnrollmentConsentChecked] = useState(false);
  const [faceConsentChecked, setFaceConsentChecked] = useState(false);
  const [cardConsentChecked, setCardConsentChecked] = useState(false);
  const [consentValidationError, setConsentValidationError] = useState(false);
  const [expandedLearnMore, setExpandedLearnMore] = useState(null);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [showCardVerificationModal, setShowCardVerificationModal] = useState(false);
  const [cardVerificationStep, setCardVerificationStep] = useState('method');
  const verificationInputRefs = useRef([]);
  const [cardVerificationCode, setCardVerificationCode] = useState('');
  const [cardVerificationError, setCardVerificationError] = useState('');
  const [cardVerificationStatus, setCardVerificationStatus] = useState('');
  const [cardVerificationRecords, setCardVerificationRecords] = useState({});
  const [lockoutNotificationCardId, setLockoutNotificationCardId] = useState(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [enrollOriginScreen, setEnrollOriginScreen] = useState('payment-selection-first-multi');
  const [showUnenrollModal, setShowUnenrollModal] = useState(false);
  const [unenrollMode, setUnenrollMode] = useState('cards');
  const [unenrollTargetCards, setUnenrollTargetCards] = useState([]);
  const [unenrollResult, setUnenrollResult] = useState(null);
  const [selectedUnenrollCards, setSelectedUnenrollCards] = useState([]);
  const [faceVerificationEnrolledDate, setFaceVerificationEnrolledDate] = useState(null);
  const [faceVerificationRemovedDate, setFaceVerificationRemovedDate] = useState(null);
  const [showFaceUnenrollNotification, setShowFaceUnenrollNotification] = useState(false);
  const [isReenrollingFaceVerification, setIsReenrollingFaceVerification] = useState(false);
  const [recentlyAddedCardId, setRecentlyAddedCardId] = useState(null);
  const [defaultUpdateErrorCardId, setDefaultUpdateErrorCardId] = useState(null);
  const [crossUserConflict, setCrossUserConflict] = useState(null); // { cardId, ownerUserId } | null
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [cameraStatus, setCameraStatus] = useState('NO_FACE_DETECTED');
  const [cameraMessage, setCameraMessage] = useState('No face detected. Please move into frame.');
  const [showGuides, setShowGuides] = useState(true);
  const [disableOnInvalid, setDisableOnInvalid] = useState(true);
  const shouldInitCameraRef = useRef(false);
  const captureElementRef = useRef(null);

  // core.js touches document at import time, so it can only run after hydration.
  useEffect(() => {
    import(/* webpackMode: "eager" */'../lib/core.js');
  }, []);
  const activeScreen = screens[stepIndex];
  useEffect(() => {
    if (activeScreen.key !== 'payment-selection-first-multi') {
      setLockoutNotificationCardId(null);
    }
  }, [activeScreen.key]);
  useEffect(() => {
    if (!currentUser) {
      setCardVerificationRecords({});
      return;
    }
    try {
      const recordsByUser = JSON.parse(localStorage.getItem(CARD_VERIFICATION_STORAGE_KEY)) || {};
      setCardVerificationRecords(recordsByUser[currentUser] || {});
    } catch {
      setCardVerificationRecords({});
    }
  }, [currentUser]);
  useEffect(() => {
    if (!currentUser) {
      setFaceVerificationEnrolledDate(null);
      setFaceVerificationRemovedDate(null);
      return;
    }
    try {
      const statuses = JSON.parse(localStorage.getItem(FACE_VERIFICATION_STATUS_STORAGE_KEY)) || {};
      const storedStatus = statuses[currentUser] || {};
      const storedCards = loadAllEnrollments()[currentUser] || [];
      const hasEnrolledCards = storedCards.some(cardId => cards.some(card => card.id === cardId));
      const enrolledDate = hasEnrolledCards ? storedStatus.enrolledDate || formatFaceVerificationDate() : null;
      const removedDate = hasEnrolledCards ? null : storedStatus.removedDate || null;
      setFaceVerificationEnrolledDate(enrolledDate);
      setFaceVerificationRemovedDate(removedDate);
      if (hasEnrolledCards && !storedStatus.enrolledDate) {
        statuses[currentUser] = {
          enrolledDate
        };
        localStorage.setItem(FACE_VERIFICATION_STATUS_STORAGE_KEY, JSON.stringify(statuses));
      }
    } catch {
      setFaceVerificationEnrolledDate(null);
      setFaceVerificationRemovedDate(null);
    }
  }, [currentUser]);
  useEffect(() => {
    if (activeScreen.key !== 'processing') {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      goToScreen('capture-success');
    }, 3000);
    return () => window.clearTimeout(timer);
  }, [activeScreen.key]);
  useEffect(() => {
    if (activeScreen.key !== 'adding-card-processing') {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      enrollCurrentCard();
      setRecentlyAddedCardId(selectedPaymentCard);
      goToScreen(enrollOriginScreen);
    }, 3000);
    return () => window.clearTimeout(timer);
  }, [activeScreen.key, enrollOriginScreen, selectedPaymentCard]);
  useEffect(() => {
    if (activeScreen.key !== 'face-verification-cards' || !recentlyAddedCardId) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setRecentlyAddedCardId(null);
    }, 3000);
    return () => window.clearTimeout(timer);
  }, [activeScreen.key, recentlyAddedCardId]);
  useEffect(() => {
    if (!showFaceUnenrollNotification) return undefined;
    const timer = window.setTimeout(() => setShowFaceUnenrollNotification(false), 3000);
    return () => window.clearTimeout(timer);
  }, [showFaceUnenrollNotification]);
  const completeMycaLogin = (storedUserId, currentUrl) => {
    const allEnrollments = loadAllEnrollments();
    sessionStorage.removeItem(MYCA_PENDING_LOGIN_KEY);
    sessionStorage.removeItem(MYCA_PENDING_USER_KEY);
    currentUrl.searchParams.delete(mycaAuthConfig.returnParam);
    window.history.replaceState({}, '', currentUrl.toString());
    setLoginError('');
    setCurrentUser(storedUserId);
    setAuthProvider('myca');
    setEnrolledCards(allEnrollments[storedUserId] || []);
    goToScreen('consent');
  };
  const redirectToMycaLogin = storedUserId => {
    const loginUrl = resolveMycaLoginUrl('client', window.location.hostname);
    if (!loginUrl) {
      setLoginError('MYCA login is not configured for this environment.');
      return;
    }
    const returnUrl = new URL(window.location.href);
    returnUrl.searchParams.set(mycaAuthConfig.returnParam, '1');
    sessionStorage.setItem(MYCA_PENDING_LOGIN_KEY, '1');
    sessionStorage.setItem(MYCA_PENDING_USER_KEY, storedUserId);
    const redirectUrl = new URL(loginUrl);
    redirectUrl.searchParams.set('DestPage', returnUrl.toString());
    window.location.href = redirectUrl.toString();
  };
  useEffect(() => {
    if (typeof window === 'undefined' || currentUser || stepIndex !== 0) {
      return;
    }
    const currentUrl = new URL(window.location.href);
    const isMycaReturn = currentUrl.searchParams.get(mycaAuthConfig.returnParam) === '1';
    const hasPendingLogin = sessionStorage.getItem(MYCA_PENDING_LOGIN_KEY) === '1';
    if (!isMycaReturn || !hasPendingLogin) {
      return;
    }
    const storedUserId = sessionStorage.getItem(MYCA_PENDING_USER_KEY) || mycaAuthConfig.defaultUserId;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), MYCA_LOGIN_TIMEOUT_MS);
    const verifyMycaSession = async () => {
      try {
        const response = await fetch(currentUrl.toString(), {
          credentials: 'include',
          signal: controller.signal
        });
        clearTimeout(timer);
        if (!response.ok) {
          if (response.status === 401) {
            redirectToMycaLogin(storedUserId);
            return;
          }
          setLoginError('Unable to verify MYCA login. Please try again.');
          return;
        }
        completeMycaLogin(storedUserId, currentUrl);
      } catch (error) {
        clearTimeout(timer);
        if ((error === null || error === void 0 ? void 0 : error.name) === 'AbortError') {
          setLoginError('MYCA login verification timed out. Please try again.');
          return;
        }
        setLoginError('Unable to verify MYCA login. Please try again.');
      }
    };
    verifyMycaSession();
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [currentUser, stepIndex]);

  // Initialize camera on capture screen (screen index 2)
  useEffect(() => {
    if (stepIndex === 2) {
      shouldInitCameraRef.current = true;
      setIsCameraReady(false);
      setCameraStatus('NO_FACE_DETECTED');
      setCameraMessage('No face detected. Please move into frame.');
    } else {
      shouldInitCameraRef.current = false;
    }
    return () => {
      shouldInitCameraRef.current = false;
    };
  }, [stepIndex]);
  useEffect(() => {
    const el = captureElementRef.current;
    if (!el || stepIndex !== 2) {
      return undefined;
    }
    const handleInitialize = () => {
      setIsCameraReady(true);
      setCameraStatus('LIVENESS_OK');
      setCameraMessage('Perfect. You are ready to capture.');
    };
    const handleError = event => {
      var _event$detail;
      setIsCameraReady(false);
      setCameraStatus('NO_FACE_DETECTED');
      setCameraMessage(((_event$detail = event.detail) === null || _event$detail === void 0 ? void 0 : _event$detail.message) || 'No face detected. Please move into frame.');
    };
    const handleDetection = event => {
      var _event$detail2, _event$detail3, _event$detail4;
      setCameraStatus(((_event$detail2 = event.detail) === null || _event$detail2 === void 0 ? void 0 : _event$detail2.code) || 'NO_FACE_DETECTED');
      setCameraMessage(((_event$detail3 = event.detail) === null || _event$detail3 === void 0 ? void 0 : _event$detail3.message) || 'No face detected. Please move into frame.');
      if (((_event$detail4 = event.detail) === null || _event$detail4 === void 0 ? void 0 : _event$detail4.status) === 'INVALID') {
        setIsCameraReady(true);
      }
    };
    const handleFaceCapture = async event => {
      var _event$detail5;
      const payload = ((_event$detail5 = event.detail) === null || _event$detail5 === void 0 ? void 0 : _event$detail5[0]) || event.detail || {};
      const photo = payload.photo;
      if (!photo) {
        return;
      }
      const photoData = typeof photo === 'string' ? photo : await new Promise(resolve => {
        const fileReader = new FileReader();
        fileReader.onload = () => resolve(fileReader.result);
        fileReader.readAsDataURL(photo);
      });
      setCapturedPhoto(photoData);
      setCaptureMetrics({
        byteSizeBefore: `${(photo.size || 0).toLocaleString()} B`,
        readableSize: formatByteSize(photo.size || 0),
        canvasWidth: '320px',
        canvasHeight: '240px',
        encodedDataUrlCharacters: `${String(photoData).length.toLocaleString()} chars`,
        byteSizeAfter: 'Not configured',
        payloadBytes: 'Not configured',
        payloadReadableSize: 'Not configured',
        payloadCharacters: 'Not configured',
        totalTime: 'N/A'
      });
      goToScreen('processing');
    };
    el.addEventListener('webCapInitialize', handleInitialize);
    el.addEventListener('webCapError', handleError);
    el.addEventListener('webCapFaceDetection', handleDetection);
    el.addEventListener('webCapFaceCapture', handleFaceCapture);
    return () => {
      el.removeEventListener('webCapInitialize', handleInitialize);
      el.removeEventListener('webCapError', handleError);
      el.removeEventListener('webCapFaceDetection', handleDetection);
      el.removeEventListener('webCapFaceCapture', handleFaceCapture);
    };
  }, [stepIndex]);
  const handleFaceConsentChange = event => {
    const checked = event.target.checked;
    setFaceConsentChecked(checked);
    if (checked && cardConsentChecked) {
      setConsentValidationError(false);
    }
  };
  const handleCardConsentChange = event => {
    const checked = event.target.checked;
    setCardConsentChecked(checked);
    if (checked && faceConsentChecked) {
      setConsentValidationError(false);
    }
  };
  const handlePrimaryAction = () => {
    if (!faceConsentChecked || !cardConsentChecked) {
      setConsentValidationError(true);
      return;
    }
    setConsentValidationError(false);
    setStepIndex(currentStep => currentStep + 1);
  };
  const goToScreen = screenKey => {
    setStepIndex(screens.findIndex(screen => screen.key === screenKey));
  };
  const goToPaymentSelection = () => {
    setSelectedPaymentCard('amex-blue');
    goToScreen('payment-selection');
  };
  const goToPaymentSelectionFirstSingle = () => {
    setSelectedPaymentCard('amex-blue');
    setEnrollOriginScreen('payment-selection-first-single');
    goToScreen('payment-selection-first-single');
  };
  const goToPaymentSelectionReturningSingle = () => {
    setSelectedPaymentCard('amex-blue');
    setEnrollOriginScreen('payment-selection-returning-single');
    goToScreen('payment-selection-returning-single');
  };
  const goToPaymentSelectionFirstMulti = () => {
    setSelectedPaymentCard(defaultPaymentCard);
    setEnrollOriginScreen('payment-selection-first-multi');
    goToScreen('payment-selection-first-multi');
  };
  const goToPaymentSelectionMulti = () => {
    setSelectedPaymentCard(defaultPaymentCard);
    setEnrollOriginScreen('payment-selection-multi');
    goToScreen('payment-selection-multi');
  };
  const goToPaymentNoEligible = () => goToScreen('payment-no-eligible');
  const goToPaymentProcessing = () => goToScreen('payment-processing');
  const goToPaymentApproved = () => {
    enrollCurrentCard();
    goToScreen('payment-approved');
  };
  const goToPaymentDeclined = () => goToScreen('payment-declined');
  const goToNoMatchScenarios = () => goToScreen('match-not-found-scenarios');
  const goToNotEnrolledScenario = () => goToScreen('verifone-not-enrolled');
  const goToFailedVerificationScenario = () => goToScreen('failed-verification');
  const startOver = () => {
    setCapturedPhoto(null);
    setCaptureMetrics(null);
    setSelectedPaymentCard(defaultPaymentCard);
    setEnrolledCards([]);
    setEnrollmentConsentChecked(false);
    setCurrentUser(null);
    setLoginUsername('');
    setLoginPassword('');
    setLoginError('');
    setStepIndex(0);
  };
  const enrollCurrentCard = () => {
    const hadEnrolledCards = enrolledCards.some(cardId => cards.some(card => card.id === cardId));
    const updated = [...new Set([...enrolledCards, selectedPaymentCard])];
    setEnrolledCards(updated);
    const all = loadAllEnrollments();
    all[currentUser] = updated;
    saveAllEnrollments(all);
    if (!hadEnrolledCards && updated.some(cardId => cards.some(card => card.id === cardId))) {
      setFaceVerificationDates(formatFaceVerificationDate(), null);
    }
  };
  const cards = [{
    id: 'amex-blue',
    name: 'Blue Cash Preferred',
    mask: '**** 5102'
  }, {
    id: 'amex-gold',
    name: 'American Express Gold',
    mask: '**** 2229'
  }, {
    id: 'amex-plat',
    name: 'Platinum Card',
    mask: '**** 1005'
  }, {
    id: 'amex-green',
    name: 'American Express Green',
    mask: '**** 3317'
  }];
  const hasFaceVerificationCards = enrolledCards.some(cardId => cards.some(card => card.id === cardId));
  const openConsentModal = () => {
    const all = loadAllEnrollments();
    const otherUser = USERS.find(user => user.id !== currentUser && (all[user.id] || []).includes(selectedPaymentCard));
    if (otherUser) {
      setCrossUserConflict({
        cardId: selectedPaymentCard,
        ownerUserId: otherUser.id
      });
      return;
    }
    setConsentChecked(false);
    setShowConsentModal(true);
  };
  const openCardVerification = cardId => {
    const record = cardVerificationRecords[cardId] || {
      attempts: 0,
      lockedUntil: 0
    };
    if (record.lockedUntil > Date.now()) {
      return;
    }
    if (record.lockedUntil) {
      saveCardVerificationRecord(cardId, {
        attempts: 0,
        lockedUntil: 0
      });
    }
    setSelectedPaymentCard(cardId);
    setRecentlyAddedCardId(null);
    setDefaultUpdateErrorCardId(null);
    setCardVerificationCode('');
    setCardVerificationError('');
    setCardVerificationStatus('');
    setCardVerificationStep('method');
    setShowCardVerificationModal(true);
  };
  const isCardVerificationLocked = cardId => {
    var _cardVerificationReco;
    return (((_cardVerificationReco = cardVerificationRecords[cardId]) === null || _cardVerificationReco === void 0 ? void 0 : _cardVerificationReco.lockedUntil) || 0) > Date.now();
  };
  const saveCardVerificationRecord = (cardId, record) => {
    const nextRecords = {
      ...cardVerificationRecords,
      [cardId]: record
    };
    setCardVerificationRecords(nextRecords);
    try {
      const recordsByUser = JSON.parse(localStorage.getItem(CARD_VERIFICATION_STORAGE_KEY)) || {};
      recordsByUser[currentUser] = nextRecords;
      localStorage.setItem(CARD_VERIFICATION_STORAGE_KEY, JSON.stringify(recordsByUser));
    } catch {
      return;
    }
  };
  const recordIncorrectCardVerification = () => {
    var _cardVerificationReco2;
    const attempts = (((_cardVerificationReco2 = cardVerificationRecords[selectedPaymentCard]) === null || _cardVerificationReco2 === void 0 ? void 0 : _cardVerificationReco2.attempts) || 0) + 1;
    const lockedUntil = attempts >= 3 ? Date.now() + CARD_VERIFICATION_LOCK_MS : 0;
    saveCardVerificationRecord(selectedPaymentCard, {
      attempts,
      lockedUntil
    });
    setCardVerificationStatus('error');
    setCardVerificationError('Code incorrect');
    if (attempts >= 3) {
      setLockoutNotificationCardId(selectedPaymentCard);
      setShowCardVerificationModal(false);
    }
  };
  const unlockCardVerifications = () => {
    const nextRecords = {
      ...cardVerificationRecords
    };
    cards.forEach(card => {
      if (isCardVerificationLocked(card.id)) {
        nextRecords[card.id] = {
          attempts: 0,
          lockedUntil: 0
        };
      }
    });
    setCardVerificationRecords(nextRecords);
    setLockoutNotificationCardId(null);
    try {
      const recordsByUser = JSON.parse(localStorage.getItem(CARD_VERIFICATION_STORAGE_KEY)) || {};
      recordsByUser[currentUser] = nextRecords;
      localStorage.setItem(CARD_VERIFICATION_STORAGE_KEY, JSON.stringify(recordsByUser));
    } catch {
      return;
    }
  };
  const updateDefaultPaymentCard = cardId => {
    if (cardId === DEFAULT_UPDATE_FAILURE_CARD_ID) {
      setRecentlyAddedCardId(null);
      setDefaultUpdateErrorCardId(cardId);
      return;
    }
    setDefaultPaymentCard(cardId);
    setDefaultUpdateErrorCardId(null);
  };
  const verifyCardCode = () => {
    saveCardVerificationRecord(selectedPaymentCard, {
      attempts: 0,
      lockedUntil: 0
    });
    setCardVerificationStatus('success');
    setShowCardVerificationModal(false);
    setCardVerificationCode('');
    setCardVerificationError('');
    setCardVerificationStatus('');
    goToScreen('adding-card-processing');
  };
  const openUnenrollModal = (cardIds, mode) => {
    setUnenrollTargetCards(cardIds);
    setSelectedUnenrollCards(cardIds); // pre-select all enrolled cards passed in
    setUnenrollResult(null);
    setUnenrollMode(mode);
    setShowUnenrollModal(true);
  };
  const openUnenrollModalForCurrentScreen = () => {
    var _screens$stepIndex;
    // Determine enrolled cards visible on the current screen
    const screenKey = (_screens$stepIndex = screens[stepIndex]) === null || _screens$stepIndex === void 0 ? void 0 : _screens$stepIndex.key;
    const singleScreens = ['payment-selection-first-single', 'payment-selection-returning-single'];
    let visibleEnrolled = [];
    if (singleScreens.includes(screenKey)) {
      visibleEnrolled = enrolledCards.filter(id => id === 'amex-blue');
    } else {
      visibleEnrolled = enrolledCards.filter(id => cards.some(c => c.id === id));
    }
    if (visibleEnrolled.length > 0) {
      openUnenrollModal(visibleEnrolled, screenKey === 'face-verification-status' ? 'face-profile' : 'cards');
    }
  };
  const setFaceVerificationDates = (enrolledDate, removedDate) => {
    setFaceVerificationEnrolledDate(enrolledDate);
    setFaceVerificationRemovedDate(removedDate);
    try {
      const statuses = JSON.parse(localStorage.getItem(FACE_VERIFICATION_STATUS_STORAGE_KEY)) || {};
      statuses[currentUser] = {
        enrolledDate,
        removedDate
      };
      localStorage.setItem(FACE_VERIFICATION_STATUS_STORAGE_KEY, JSON.stringify(statuses));
    } catch {
      return;
    }
  };
  const confirmUnenrollSuccess = () => {
    if (unenrollMode === 'face-profile') {
      const allEnrollments = loadAllEnrollments();
      allEnrollments[currentUser] = [];
      saveAllEnrollments(allEnrollments);
      setEnrolledCards([]);
      setUnenrollTargetCards([]);
      setSelectedUnenrollCards([]);
      setUnenrollResult(null);
      setShowUnenrollModal(false);
      setFaceVerificationDates(null, formatFaceVerificationDate());
      setShowFaceUnenrollNotification(true);
      return;
    }
    const remainingCards = enrolledCards.filter(id => !selectedUnenrollCards.includes(id));
    setEnrolledCards(remainingCards);
    const allEnrollments = loadAllEnrollments();
    allEnrollments[currentUser] = remainingCards;
    saveAllEnrollments(allEnrollments);
    if (!remainingCards.some(cardId => cards.some(card => card.id === cardId))) {
      setFaceVerificationDates(null, formatFaceVerificationDate());
    }
    setUnenrollTargetCards([]);
    setSelectedUnenrollCards([]);
    setUnenrollResult('success');
  };
  const closeUnenrollModal = () => {
    setShowUnenrollModal(false);
    setUnenrollResult(null);
    setUnenrollTargetCards([]);
    setUnenrollMode('cards');
  };
  const toggleUnenrollSelect = cardId => {
    setSelectedUnenrollCards(prev => prev.includes(cardId) ? prev.filter(id => id !== cardId) : [...prev, cardId]);
  };
  const handleDemoLogin = () => {
    const user = USERS[0];
    setLoginError('');
    setCurrentUser(user.id);
    setAuthProvider('demo');
    const all = loadAllEnrollments();
    setEnrolledCards(all[user.id] || []);
    goToScreen('consent');
  };
  const handleLogout = () => {
    const shouldLogoutFromMyca = authProvider === 'myca';
    const returnUrl = new URL(window.location.href);
    returnUrl.searchParams.delete(mycaAuthConfig.returnParam);
    sessionStorage.removeItem(MYCA_PENDING_LOGIN_KEY);
    sessionStorage.removeItem(MYCA_PENDING_USER_KEY);
    setCurrentUser(null);
    setAuthProvider(null);
    setEnrolledCards([]);
    setLoginUsername('');
    setLoginPassword('');
    setLoginError('');
    setCapturedPhoto(null);
    setCaptureMetrics(null);
    setFaceConsentChecked(false);
    setCardConsentChecked(false);
    setConsentValidationError(false);
    setShowConsentModal(false);
    setShowCardVerificationModal(false);
    setShowUnenrollModal(false);
    setIsReenrollingFaceVerification(false);
    setShowFaceUnenrollNotification(false);
    setStepIndex(0);
    if (shouldLogoutFromMyca) {
      const logoutUrl = new URL(resolveMycaLogoutUrl('client', window.location.hostname));
      logoutUrl.searchParams.set('DestPage', returnUrl.toString());
      window.location.href = logoutUrl.toString();
      return;
    }
    window.history.replaceState({}, '', returnUrl.toString());
  };
  const handleLogin = () => {
    redirectToMycaLogin(loginUsername.trim() || mycaAuthConfig.defaultUserId);
  };
  const beginFaceVerificationReenrollment = () => {
    setFaceConsentChecked(false);
    setCardConsentChecked(false);
    setConsentValidationError(false);
    setIsReenrollingFaceVerification(true);
    goToScreen('consent');
  };
  const continueAfterFaceCapture = () => {
    if (isReenrollingFaceVerification) setIsReenrollingFaceVerification(false);
    goToPaymentSelectionFirstMulti();
  };
  const confirmConsent = () => {
    setShowConsentModal(false);
    goToPaymentProcessing();
  };
  const cancelConsent = () => {
    setShowConsentModal(false);
  };
  return /*#__PURE__*/React.createElement("main", {
    className: `app-shell${activeScreen.key === 'login' ? ' login-app-shell' : ''}${activeScreen.key === 'capture' ? ' capture-app-shell' : ''}${activeScreen.key === 'processing' ? ' processing-app-shell' : ''}${activeScreen.key === 'capture-success' ? ' capture-success-app-shell' : ''}${['face-verification-status', 'face-verification-cards'].includes(activeScreen.key) ? ' face-verification-status-app-shell' : ''}`
  }, currentUser ? /*#__PURE__*/React.createElement("header", {
    className: "app-auth-header"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "myca-logout-button",
    onClick: handleLogout
  }, "Log out")) : null, crossUserConflict ? /*#__PURE__*/React.createElement("div", {
    className: "consent-modal-backdrop",
    role: "dialog",
    "aria-modal": "true",
    "aria-label": "Card already enrolled"
  }, /*#__PURE__*/React.createElement("div", {
    className: "consent-modal"
  }, /*#__PURE__*/React.createElement("h3", {
    className: "consent-modal-title",
    style: {
      color: '#c82000'
    }
  }, "Card already activated"), /*#__PURE__*/React.createElement("p", {
    className: "consent-modal-copy"
  }, (() => {
    const card = cards.find(c => c.id === crossUserConflict.cardId);
    return `${card.name} (${card.mask}) is already activated with login ID ${crossUserConflict.ownerUserId}. You can still activate it with your current login (${currentUser})`;
  })()), /*#__PURE__*/React.createElement("div", {
    className: "consent-modal-button-group"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "payment-primary-button",
    onClick: () => {
      setCrossUserConflict(null);
      setConsentChecked(false);
      setShowConsentModal(true);
    }
  }, "Continue to activate"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "payment-secondary-button",
    onClick: () => setCrossUserConflict(null)
  }, "Cancel")))) : null, showConsentModal ? /*#__PURE__*/React.createElement("div", {
    className: "consent-modal-backdrop",
    role: "dialog",
    "aria-modal": "true",
    "aria-label": "Activation consent"
  }, /*#__PURE__*/React.createElement("div", {
    className: "consent-modal"
  }, /*#__PURE__*/React.createElement("h3", {
    className: "consent-modal-title"
  }, "Consent to Activate"), /*#__PURE__*/React.createElement("div", {
    className: "consent-modal-lines",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("span", null), /*#__PURE__*/React.createElement("span", null), /*#__PURE__*/React.createElement("span", null)), /*#__PURE__*/React.createElement("p", {
    className: "consent-modal-copy"
  }, "By activating this card, you agree to the American Express terms and conditions."), /*#__PURE__*/React.createElement("label", {
    className: "consent-modal-check-row"
  }, /*#__PURE__*/React.createElement("input", {
    type: "checkbox",
    className: "consent-checkbox",
    checked: consentChecked,
    onChange: e => setConsentChecked(e.target.checked)
  }), /*#__PURE__*/React.createElement("span", null, "I agree to the terms and conditions")), /*#__PURE__*/React.createElement("div", {
    className: "consent-modal-button-group"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "payment-primary-button",
    onClick: confirmConsent,
    disabled: !consentChecked
  }, "Confirm & Activate"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "payment-secondary-button",
    onClick: cancelConsent
  }, "Cancel")))) : null, showCardVerificationModal ? /*#__PURE__*/React.createElement("div", {
    className: "consent-modal-backdrop",
    role: "dialog",
    "aria-modal": "true",
    "aria-label": "Verify to add your card"
  }, /*#__PURE__*/React.createElement("div", {
    className: `card-verification-modal${cardVerificationStep === 'code' || cardVerificationStep === 'unable' ? ' card-verification-code-modal' : ''}`
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "card-verification-close",
    "aria-label": "Close verification",
    onClick: () => setShowCardVerificationModal(false)
  }, "\xD7"), cardVerificationStep === 'method' ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "card-verification-header"
  }, /*#__PURE__*/React.createElement("h3", null, "Verify to Add Your Card")), /*#__PURE__*/React.createElement("p", {
    className: "card-verification-description"
  }, "Choose a verification method to securely add this Card to your wallet."), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "card-verification-method",
    onClick: () => setCardVerificationStep('code')
  }, /*#__PURE__*/React.createElement("span", {
    className: "card-verification-method-icon"
  }, /*#__PURE__*/React.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M17.5 0C18.3284 0 19 0.671573 19 1.5V22.5C19 23.3284 18.3284 24 17.5 24H6.5C5.67157 24 5 23.3284 5 22.5V1.5C5 0.671573 5.67157 9.6639e-8 6.5 0H17.5ZM6.5 22.5H17.5V1.5H15.5C15.5 2.05228 15.0523 2.5 14.5 2.5H9.5C8.94772 2.5 8.5 2.05228 8.5 1.5H6.5V22.5ZM12 19C12.5523 19 13 19.4477 13 20C13 20.5523 12.5523 21 12 21C11.4477 21 11 20.5523 11 20C11 19.4477 11.4477 19 12 19Z",
    fill: "#006FCF"
  }))), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("strong", null, "Text Message"), /*#__PURE__*/React.createElement("small", null, "+1-***-***-2904", /*#__PURE__*/React.createElement("br", null), "Message and data rates may apply")), /*#__PURE__*/React.createElement(IconChevronRight, {
    className: "card-verification-chevron"
  }))) : cardVerificationStep === 'code' ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "card-verification-header"
  }, /*#__PURE__*/React.createElement("h3", null, "Enter Verification", /*#__PURE__*/React.createElement("br", null), "Code")), /*#__PURE__*/React.createElement("p", null, "This is the 6-digit code we texted to +1", /*#__PURE__*/React.createElement("br", null), "***-***-0405."), /*#__PURE__*/React.createElement("div", {
    className: "verification-code-boxes",
    "aria-label": "Enter 6-digit verification code"
  }, Array.from({
    length: 6
  }, (_, index) => /*#__PURE__*/React.createElement("input", {
    key: index,
    ref: element => {
      verificationInputRefs.current[index] = element;
    },
    value: cardVerificationCode[index] || '',
    onChange: event => {
      const digit = event.target.value.replace(/\D/g, '').slice(-1);
      const nextCode = cardVerificationCode.padEnd(6, ' ').split('');
      nextCode[index] = digit;
      const updatedCode = nextCode.join('').replace(/\s+$/, '');
      setCardVerificationCode(updatedCode);
      if (updatedCode === '123456') {
        setCardVerificationError('');
        setCardVerificationStatus('success');
      } else if (/^\d{6}$/.test(updatedCode)) {
        if (cardVerificationStatus !== 'error') recordIncorrectCardVerification();
      } else {
        setCardVerificationError('');
        setCardVerificationStatus('');
      }
      if (digit && index < 5) {
        var _verificationInputRef;
        (_verificationInputRef = verificationInputRefs.current[index + 1]) === null || _verificationInputRef === void 0 || _verificationInputRef.focus();
      }
    },
    onKeyDown: event => {
      if (event.key === 'Backspace' && !cardVerificationCode[index] && index > 0) {
        var _verificationInputRef2;
        (_verificationInputRef2 = verificationInputRefs.current[index - 1]) === null || _verificationInputRef2 === void 0 || _verificationInputRef2.focus();
      }
    },
    inputMode: "numeric",
    maxLength: 1,
    "aria-label": `Verification digit ${index + 1}`,
    "aria-invalid": cardVerificationStatus === 'error',
    className: `verification-code-box${cardVerificationStatus ? ` verification-code-box-${cardVerificationStatus}` : ''}`
  }))), !cardVerificationStatus ? /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "card-verification-resend"
  }, "Send a new code") : null, cardVerificationStatus === 'success' ? /*#__PURE__*/React.createElement("p", {
    className: "card-verification-success",
    role: "status"
  }, "Code verified") : null, cardVerificationError ? /*#__PURE__*/React.createElement("p", {
    className: "card-verification-error"
  }, cardVerificationError) : null, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "card-verification-button",
    onClick: verifyCardCode,
    disabled: cardVerificationCode.length !== 6 || cardVerificationStatus === 'error'
  }, "Continue"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "card-verification-secondary",
    onClick: () => {
      setCardVerificationStep('unable');
      setCardVerificationError('');
      setCardVerificationStatus('');
    }
  }, "Verify Another Way")) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "card-verification-header"
  }, /*#__PURE__*/React.createElement("h3", null, "Enter Verification", /*#__PURE__*/React.createElement("br", null), "Code")), /*#__PURE__*/React.createElement("div", {
    className: "card-verification-unable"
  }, /*#__PURE__*/React.createElement("span", {
    className: "card-verification-warning-icon",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: "48",
    height: "48",
    viewBox: "0 0 48 44",
    fill: "none"
  }, /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M19.918 2.54308C21.071 0.248077 23.833 -0.660923 26.088 0.513077C26.8689 0.921438 27.5167 1.54461 27.955 2.30908L28.081 2.54308L47.496 37.2061C48.649 39.5011 47.756 42.3141 45.503 43.4881C44.9398 43.7818 44.321 43.9535 43.687 43.9921L43.415 44.0001H4.584C2.052 44.0001 0 41.9001 0 39.3321C-1.39664e-05 38.6869 0.131675 38.0486 0.387 37.4561L0.504 37.2061L19.918 2.54308ZM24 32.0001C22.8954 32.0001 22 32.8956 22 34.0001C22 35.1047 22.8954 36.0001 24 36.0001C25.1046 36.0001 26 35.1047 26 34.0001C26 32.8956 25.1046 32.0001 24 32.0001ZM23.768 15.0124L23.952 14.9994L23.953 14.9984L24.158 15.0034L24.318 15.0234C25.221 15.1634 25.922 15.8904 25.994 16.7904L26 16.9424L25.583 27.5194L25.57 27.6634C25.467 28.4194 24.8 28.9994 24 28.9994C23.15 28.9994 22.45 28.3454 22.418 27.5194L22.002 17.0174V16.8654C22.04 15.9124 22.792 15.1124 23.768 15.0124Z",
    fill: "#B41601"
  }))), /*#__PURE__*/React.createElement("h3", null, "Unable to Verify Your Card"), /*#__PURE__*/React.createElement("p", {
    className: "card-verification-unable-copy"
  }, "We couldn\u2019t verify your Card ending in ", cards.find(card => card.id === selectedPaymentCard).mask.slice(-4), ", so it wasn\u2019t added to your Face Verification wallet."), /*#__PURE__*/React.createElement("p", {
    className: "card-verification-unable-copy"
  }, "For help, call the number on the back of your Card."), /*#__PURE__*/React.createElement("div", {
    className: "card-verification-unable-divider"
  }), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "card-verification-button",
    onClick: () => setShowCardVerificationModal(false)
  }, "Back to Your Cards"))))) : null, showUnenrollModal ? /*#__PURE__*/React.createElement("div", {
    className: "consent-modal-backdrop",
    role: "dialog",
    "aria-modal": "true",
    "aria-label": "Unenroll card confirmation"
  }, /*#__PURE__*/React.createElement("div", {
    className: `consent-modal unenroll-modal${unenrollMode === 'face-profile' ? ' unenroll-profile-modal' : ''}`
  }, unenrollResult === null ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("h3", {
    className: "consent-modal-title"
  }, unenrollMode === 'face-profile' ? 'Unenroll Face Verification?' : 'Unenroll cards'), /*#__PURE__*/React.createElement("p", {
    className: "consent-modal-copy"
  }, unenrollMode === 'face-profile' ? 'Your biometric data will be deleted and your eligible card(s) will be unenrolled. You can re-enroll anytime.' : 'Select the cards you want to unenroll from biometric payments.'), unenrollMode === 'face-profile' ? /*#__PURE__*/React.createElement("div", {
    className: "unenroll-modal-divider"
  }) : /*#__PURE__*/React.createElement("div", {
    className: "unenroll-card-list"
  }, unenrollTargetCards.map((cardId, imageIndex) => {
    const card = cards.find(item => item.id === cardId);
    const isChecked = selectedUnenrollCards.includes(cardId);
    return /*#__PURE__*/React.createElement("label", {
      key: cardId,
      className: "unenroll-card-row"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      className: "consent-checkbox",
      checked: isChecked,
      onChange: () => toggleUnenrollSelect(cardId)
    }), /*#__PURE__*/React.createElement("img", {
      src: cardImageAt(imageIndex),
      alt: "",
      className: "pcard-img"
    }), /*#__PURE__*/React.createElement("div", {
      className: "pcard-details"
    }, /*#__PURE__*/React.createElement("span", {
      className: "pcard-name"
    }, card.name), /*#__PURE__*/React.createElement("span", {
      className: "pcard-mask"
    }, card.mask)));
  })), /*#__PURE__*/React.createElement("div", {
    className: "consent-modal-button-group"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "payment-unenroll-button",
    onClick: confirmUnenrollSuccess,
    disabled: selectedUnenrollCards.length === 0
  }, "Unenroll"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "payment-secondary-button",
    onClick: closeUnenrollModal
  }, "Cancel"))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "unenroll-result-icon unenroll-result-success",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("span", {
    className: "result-check"
  })), /*#__PURE__*/React.createElement("h3", {
    className: "consent-modal-title",
    style: {
      color: '#008640'
    }
  }, "Unenrolled successfully"), /*#__PURE__*/React.createElement("p", {
    className: "consent-modal-copy"
  }, "The selected cards have been unenrolled from biometric payments."), /*#__PURE__*/React.createElement("div", {
    className: "consent-modal-button-group"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "payment-primary-button",
    onClick: closeUnenrollModal
  }, "Ok"))))) : null, /*#__PURE__*/React.createElement("section", {
    className: "simple-flow"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flow-content"
  }, activeScreen.key === 'login' ? /*#__PURE__*/React.createElement("div", {
    className: "login-shell",
    "aria-label": "Login form"
  }, loginError && /*#__PURE__*/React.createElement("p", {
    className: "login-error"
  }, loginError), /*#__PURE__*/React.createElement("div", {
    className: "submit-button-group"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "consent-enroll-button",
    onClick: handleDemoLogin
  }, "Demo Login"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "consent-enroll-button",
    onClick: handleLogin
  }, "Continue with MYCA Login"))) : null, activeScreen.key === 'capture' ? /*#__PURE__*/React.createElement("div", {
    className: "capture-shell",
    "aria-label": "Photo capture screen"
  }, /*#__PURE__*/React.createElement("div", {
    className: "capture-top-bar"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "capture-nav-button capture-nav-back",
    "aria-label": "Back to consent",
    onClick: () => setStepIndex(1)
  }, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  })), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "capture-nav-button capture-nav-help",
    "aria-label": "Camera help"
  }, "?"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "capture-nav-button capture-nav-close",
    "aria-label": "Close capture",
    onClick: () => setStepIndex(0)
  }, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  }))), /*#__PURE__*/React.createElement("verifone-face-capture", {
    ref: captureElementRef,
    init_button_text: "Pay With A Smile",
    open_button_text: "Take Photo",
    show_guides: true,
    disable_on_invalid: true,
    style: {
      width: '100%'
    }
  })) : null, activeScreen.key === 'consent' ? /*#__PURE__*/React.createElement("div", {
    className: "consent-shell",
    "aria-label": "Biometric consent form"
  }, /*#__PURE__*/React.createElement("div", {
    className: "consent-panel"
  }, /*#__PURE__*/React.createElement("div", {
    className: "consent-welcome"
  }, "Welcome, ", currentUser), /*#__PURE__*/React.createElement("h2", {
    className: "consent-title"
  }, "Set up Face Verification."), /*#__PURE__*/React.createElement("p", {
    className: "consent-copy"
  }, "Set up Face Verification to securely pay with your eligible American Express Card at participating checkouts."), /*#__PURE__*/React.createElement("div", {
    className: "consent-benefits",
    "aria-label": "Face verification benefits"
  }, /*#__PURE__*/React.createElement("div", {
    className: "consent-benefit-row"
  }, /*#__PURE__*/React.createElement("div", {
    className: "consent-benefit-icon",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement(IconAccount, {
    className: "consent-dls-icon"
  })), /*#__PURE__*/React.createElement("div", {
    className: "consent-benefit-copy"
  }, /*#__PURE__*/React.createElement("span", {
    className: "consent-benefit-heading"
  }, "One Photo"), /*#__PURE__*/React.createElement("span", {
    className: "consent-benefit-text"
  }, "Take one photo to securely set up your Face Verification."))), /*#__PURE__*/React.createElement("div", {
    className: "consent-benefit-row"
  }, /*#__PURE__*/React.createElement("div", {
    className: "consent-benefit-icon",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement(IconInsurance, {
    className: "consent-dls-icon"
  })), /*#__PURE__*/React.createElement("div", {
    className: "consent-benefit-copy"
  }, /*#__PURE__*/React.createElement("span", {
    className: "consent-benefit-heading"
  }, "Privacy Built In"), /*#__PURE__*/React.createElement("span", {
    className: "consent-benefit-text"
  }, "Your information is encrypted and protected by American Express."))), /*#__PURE__*/React.createElement("div", {
    className: "consent-benefit-row"
  }, /*#__PURE__*/React.createElement("div", {
    className: "consent-benefit-icon",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement(IconFilter, {
    className: "consent-dls-icon"
  })), /*#__PURE__*/React.createElement("div", {
    className: "consent-benefit-copy"
  }, /*#__PURE__*/React.createElement("span", {
    className: "consent-benefit-heading"
  }, "You\u2019re In Control"), /*#__PURE__*/React.createElement("span", {
    className: "consent-benefit-text"
  }, "Turn off Face Verification and delete your information anytime.")))), /*#__PURE__*/React.createElement("label", {
    className: "consent-checkbox-row",
    htmlFor: "face-consent-checkbox"
  }, /*#__PURE__*/React.createElement("input", {
    id: "face-consent-checkbox",
    type: "checkbox",
    checked: faceConsentChecked,
    onChange: handleFaceConsentChange
  }), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("strong", {
    className: "consent-checkbox-lead"
  }, "Use my photo for Face Verification."), ' ', /*#__PURE__*/React.createElement("span", {
    className: "consent-checkbox-agreement"
  }, "I agree to the use of my facial image and biometric data to set up and use Face Verification."))), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "consent-learn-more",
    onClick: () => setExpandedLearnMore(current => current === 'face' ? null : 'face'),
    "aria-expanded": expandedLearnMore === 'face'
  }, expandedLearnMore === 'face' ? 'Show Less' : 'Learn More'), expandedLearnMore === 'face' ? /*#__PURE__*/React.createElement("div", {
    className: "consent-learn-more-content"
  }, /*#__PURE__*/React.createElement("p", null, "This processing includes:"), /*#__PURE__*/React.createElement("ul", null, /*#__PURE__*/React.createElement("li", null, "Enrollment, liveness detection, and 1:1 facial matching in the Verifone Biometrics Pilot."), /*#__PURE__*/React.createElement("li", null, "Verifone creating, storing, and using a mathematical representation of my facial characteristics while I remain enrolled."), /*#__PURE__*/React.createElement("li", null, "Raw facial images will not be stored."))) : null, /*#__PURE__*/React.createElement("label", {
    className: "consent-checkbox-row",
    htmlFor: "card-consent-checkbox"
  }, /*#__PURE__*/React.createElement("input", {
    id: "card-consent-checkbox",
    type: "checkbox",
    checked: cardConsentChecked,
    onChange: handleCardConsentChange
  }), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("strong", {
    className: "consent-checkbox-lead"
  }, "Add my eligible American Express Cards."), ' ', /*#__PURE__*/React.createElement("span", {
    className: "consent-checkbox-agreement"
  }, "I agree to add my current and future eligible Cards to Face Verification."))), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "consent-learn-more",
    onClick: () => setExpandedLearnMore(current => current === 'cards' ? null : 'cards'),
    "aria-expanded": expandedLearnMore === 'cards'
  }, expandedLearnMore === 'cards' ? 'Show Less' : 'Learn More'), expandedLearnMore === 'cards' ? /*#__PURE__*/React.createElement("div", {
    className: "consent-learn-more-content"
  }, /*#__PURE__*/React.createElement("p", null, "This processing includes:"), /*#__PURE__*/React.createElement("ul", null, /*#__PURE__*/React.createElement("li", null, "Enrollment, liveness detection, and 1:1 facial matching in the Verifone Biometrics Pilot."), /*#__PURE__*/React.createElement("li", null, "Verifone creating, storing, and using a mathematical representation of my facial characteristics while I remain enrolled."), /*#__PURE__*/React.createElement("li", null, "Raw facial images will not be stored."))) : null, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "consent-enroll-button",
    onClick: handlePrimaryAction,
    disabled: false
  }, "Set Up Face Verification"), consentValidationError ? /*#__PURE__*/React.createElement("div", {
    className: "consent-validation-error",
    role: "alert"
  }, /*#__PURE__*/React.createElement("svg", {
    className: "consent-validation-icon",
    xmlns: "http://www.w3.org/2000/svg",
    width: "24",
    height: "22",
    viewBox: "0 0 24 22",
    fill: "none",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M9.959 1.27154C10.5355 0.124039 11.9165 -0.330461 13.044 0.256539C13.4344 0.460719 13.7583 0.772304 13.9775 1.15454L14.0405 1.27154L23.748 18.603C24.3245 19.7505 23.878 21.157 22.7515 21.744C22.4699 21.8909 22.1605 21.9768 21.8435 21.996L21.7075 22H2.292C1.026 22 0 20.95 0 19.666C-6.98319e-06 19.3435 0.0658373 19.0243 0.1935 18.728L0.252 18.603L9.959 1.27154ZM12 16.0001C11.4477 16.0001 11 16.4478 11 17.0001C11 17.5524 11.4477 18.0001 12 18.0001C12.5523 18.0001 13 17.5524 13 17.0001C13 16.4478 12.5523 16.0001 12 16.0001ZM11.884 7.50621L11.976 7.49971L11.9765 7.49921L12.079 7.50171L12.159 7.51171C12.6105 7.58171 12.961 7.94521 12.997 8.39521L13 8.47121L12.7915 13.7597L12.785 13.8317C12.7335 14.2097 12.4 14.4997 12 14.4997C11.575 14.4997 11.225 14.1727 11.209 13.7597L11.001 8.50871V8.43271C11.02 7.95621 11.396 7.55621 11.884 7.50621Z",
    fill: "#B41601"
  })), /*#__PURE__*/React.createElement("span", null, "Select both \u201CUse my photo for Face Verification\u201D and \u201CAdd my eligible American Express Cards.\u201D")) : null)) : null, activeScreen.key === 'processing' ? /*#__PURE__*/React.createElement("div", {
    className: "processing-shell",
    "aria-label": "Photo processing screen"
  }, /*#__PURE__*/React.createElement("div", {
    className: "processing-identity-graphic",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("div", {
    className: "processing-identity-orbit processing-identity-orbit-outer"
  }), /*#__PURE__*/React.createElement("div", {
    className: "processing-identity-orbit processing-identity-orbit-inner"
  }), /*#__PURE__*/React.createElement("div", {
    className: "processing-identity-shield"
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 36 46"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M18 2 32 8v13c0 10-5.6 17.9-14 22C9.6 38.9 4 31 4 21V8l14-6Z",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "3",
    strokeLinejoin: "round"
  })))), /*#__PURE__*/React.createElement("h2", {
    className: "processing-title"
  }, "Securing your identity."), /*#__PURE__*/React.createElement("div", {
    className: "processing-status-list"
  }, /*#__PURE__*/React.createElement("div", {
    className: "processing-status-row"
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M7 8h10l1.5 2H21v10H3V10h2.5L7 8Z",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.7",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "12",
    cy: "15",
    r: "2.5",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.7"
  })), /*#__PURE__*/React.createElement("span", null, "Photo captured")), /*#__PURE__*/React.createElement("div", {
    className: "processing-status-row"
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24"
  }, /*#__PURE__*/React.createElement("rect", {
    x: "5",
    y: "10",
    width: "14",
    height: "11",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.7"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M8 10V7a4 4 0 0 1 8 0v3M12 14v4",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.7",
    strokeLinecap: "round"
  })), /*#__PURE__*/React.createElement("span", null, "Encrypting your template")), /*#__PURE__*/React.createElement("div", {
    className: "processing-status-row"
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M12 2.5 19 6v6.5c0 4.5-2.7 7.8-7 10-4.3-2.2-7-5.5-7-10V6l7-3.5Z",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.7",
    strokeLinejoin: "round"
  })), /*#__PURE__*/React.createElement("span", null, "Verifying identity")), /*#__PURE__*/React.createElement("div", {
    className: "processing-status-row"
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24"
  }, /*#__PURE__*/React.createElement("circle", {
    cx: "12",
    cy: "12",
    r: "9",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.7"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m8 12 2.6 2.6L16.5 9",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.7",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })), /*#__PURE__*/React.createElement("span", null, "Enrolling"))), /*#__PURE__*/React.createElement("div", {
    className: "processing-button-group processing-dev-actions"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "processing-success-button",
    onClick: () => goToScreen('capture-success')
  }, "Match found"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "processing-failed-button",
    onClick: goToNoMatchScenarios
  }, "Match not found"))) : null, activeScreen.key === 'match-not-found-scenarios' ? /*#__PURE__*/React.createElement("div", {
    className: "payment-shell",
    "aria-label": "Match not found scenario selection screen"
  }, /*#__PURE__*/React.createElement("h2", {
    className: "payment-title"
  }, "Choose Match Not Found Scenario"), /*#__PURE__*/React.createElement("p", {
    className: "payment-subtitle"
  }, "Use these buttons to open each fallback screen from the flow."), /*#__PURE__*/React.createElement("div", {
    className: "payment-button-group"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "payment-primary-button",
    onClick: goToNotEnrolledScenario
  }, "Amex not enrolled with Verifone"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "payment-primary-button",
    onClick: goToFailedVerificationScenario
  }, "Failed verification"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "payment-secondary-button",
    onClick: goToPaymentSelection
  }, "Continue to payment flow"))) : null, activeScreen.key === 'verifone-not-enrolled' ? /*#__PURE__*/React.createElement("div", {
    className: "payment-shell",
    "aria-label": "Amex not enrolled with Verifone screen"
  }, /*#__PURE__*/React.createElement("h2", {
    className: "payment-title"
  }, "Amex: Not enrolled with Verifone"), /*#__PURE__*/React.createElement("div", {
    className: "scenario-qr-box",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("span", {
    className: "scenario-qr-grid"
  })), /*#__PURE__*/React.createElement("p", {
    className: "payment-subtitle"
  }, "We cannot find you, scan QR code to enroll in Amex app."), /*#__PURE__*/React.createElement("div", {
    className: "payment-button-group"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "payment-primary-button",
    onClick: goToNoMatchScenarios
  }, "Back to scenarios"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "payment-secondary-button",
    onClick: startOver
  }, "Start over"))) : null, activeScreen.key === 'failed-verification' ? /*#__PURE__*/React.createElement("div", {
    className: "result-shell result-shell-failed",
    "aria-label": "Failed verification screen"
  }, /*#__PURE__*/React.createElement("h2", {
    className: "result-title"
  }, "Amex: Failed verification"), /*#__PURE__*/React.createElement("div", {
    className: "result-icon result-icon-failed",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("span", {
    className: "result-cross"
  })), /*#__PURE__*/React.createElement("p", {
    className: "result-copy"
  }, "No match found."), /*#__PURE__*/React.createElement("div", {
    className: "payment-button-group"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "payment-primary-button",
    onClick: goToNoMatchScenarios
  }, "Back to scenarios"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "payment-secondary-button",
    onClick: startOver
  }, "Start over"))) : null, activeScreen.key === 'payment-selection' ? /*#__PURE__*/React.createElement("div", {
    className: "payment-shell",
    "aria-label": "Payment method selection screen"
  }, /*#__PURE__*/React.createElement("h2", {
    className: "payment-title"
  }, "Payment Method Selection"), /*#__PURE__*/React.createElement("p", {
    className: "payment-subtitle"
  }, "Choose a payment method scenario."), /*#__PURE__*/React.createElement("div", {
    className: "payment-button-group"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "payment-secondary-button",
    onClick: goToPaymentSelectionFirstSingle
  }, "First-time, single card"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "payment-secondary-button",
    onClick: goToPaymentSelectionReturningSingle
  }, "Returning member, single card"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "payment-secondary-button",
    onClick: goToPaymentSelectionFirstMulti
  }, "First-time, multiple cards"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "payment-secondary-button",
    onClick: goToPaymentSelectionMulti
  }, "Returning member, multiple cards"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "payment-secondary-button",
    onClick: goToPaymentNoEligible
  }, "No eligible payment methods"))) : null, activeScreen.key === 'payment-selection-first-single' ? /*#__PURE__*/React.createElement("div", {
    className: "payment-shell",
    "aria-label": "First-time single card payment method screen"
  }, enrolledCards.includes('amex-blue') && /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "unenroll-top-link",
    onClick: openUnenrollModalForCurrentScreen
  }, "Unenroll"), /*#__PURE__*/React.createElement("div", {
    className: "pscreen-header"
  }, /*#__PURE__*/React.createElement("h2", {
    className: "payment-title"
  }, "First-time card member use, single card account.")), /*#__PURE__*/React.createElement("div", {
    className: `pcard-row ${enrolledCards.includes('amex-blue') ? 'pcard-row-enrolled' : 'pcard-row-selected'}`
  }, /*#__PURE__*/React.createElement("img", {
    src: cardImageAt(0),
    alt: "",
    className: "pcard-img"
  }), /*#__PURE__*/React.createElement("div", {
    className: "pcard-details"
  }, /*#__PURE__*/React.createElement("span", {
    className: "pcard-name"
  }, "Blue Cash Preferred"), /*#__PURE__*/React.createElement("span", {
    className: "pcard-mask"
  }, "**** 5102"), enrolledCards.includes('amex-blue') && /*#__PURE__*/React.createElement("span", {
    className: "pcard-enrolled-badge"
  }, "Enrolled"))), /*#__PURE__*/React.createElement("div", {
    className: "payment-button-group"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "payment-primary-button",
    onClick: openConsentModal,
    disabled: enrolledCards.includes('amex-blue')
  }, enrolledCards.includes('amex-blue') ? 'Already enrolled' : 'Activate'), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "payment-secondary-button",
    onClick: goToPaymentSelection
  }, "Back"))) : null, activeScreen.key === 'payment-selection-returning-single' ? /*#__PURE__*/React.createElement("div", {
    className: "payment-shell",
    "aria-label": "Returning single card payment method screen"
  }, enrolledCards.includes('amex-blue') && /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "unenroll-top-link",
    onClick: openUnenrollModalForCurrentScreen
  }, "Unenroll"), /*#__PURE__*/React.createElement("div", {
    className: "pscreen-header"
  }, /*#__PURE__*/React.createElement("h2", {
    className: "payment-title"
  }, "Returning card member, single card account.")), /*#__PURE__*/React.createElement("div", {
    className: `pcard-row ${enrolledCards.includes('amex-blue') ? 'pcard-row-enrolled' : 'pcard-row-selected'}`
  }, /*#__PURE__*/React.createElement("img", {
    src: cardImageAt(0),
    alt: "",
    className: "pcard-img"
  }), /*#__PURE__*/React.createElement("div", {
    className: "pcard-details"
  }, /*#__PURE__*/React.createElement("span", {
    className: "pcard-name"
  }, "Blue Cash Preferred"), /*#__PURE__*/React.createElement("span", {
    className: "pcard-mask"
  }, "**** 5102"), defaultPaymentCard === 'amex-blue' && /*#__PURE__*/React.createElement("span", {
    className: "pcard-default-badge"
  }, "Default"), enrolledCards.includes('amex-blue') && /*#__PURE__*/React.createElement("span", {
    className: "pcard-enrolled-badge"
  }, "Enrolled"))), /*#__PURE__*/React.createElement("div", {
    className: "payment-button-group"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "payment-primary-button",
    onClick: openConsentModal,
    disabled: enrolledCards.includes('amex-blue')
  }, enrolledCards.includes('amex-blue') ? 'Already enrolled' : 'Activate'), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "payment-secondary-button",
    onClick: goToPaymentSelection
  }, "Back"))) : null, activeScreen.key === 'payment-selection-first-multi' ? /*#__PURE__*/React.createElement("div", {
    className: "eligible-cards-shell",
    "aria-label": "Your eligible cards"
  }, enrolledCards.some(id => cards.some(card => card.id === id)) || cards.some(card => isCardVerificationLocked(card.id)) ? /*#__PURE__*/React.createElement("div", {
    className: "eligible-cards-top-actions"
  }, enrolledCards.some(id => cards.some(card => card.id === id)) ? /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "unenroll-top-link",
    onClick: openUnenrollModalForCurrentScreen
  }, "Unenroll") : null, cards.some(card => isCardVerificationLocked(card.id)) ? /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "unenroll-top-link unlock-top-link",
    onClick: unlockCardVerifications
  }, "Unlock") : null) : null, /*#__PURE__*/React.createElement("h2", {
    className: "eligible-cards-title"
  }, "Your Eligible Cards"), /*#__PURE__*/React.createElement("p", {
    className: "eligible-cards-copy"
  }, "Add an eligible Card(s) to use for biometric payments, or continue without adding one."), cards.filter(card => card.id === lockoutNotificationCardId && isCardVerificationLocked(card.id)).map(card => /*#__PURE__*/React.createElement(VerificationLockNotification, {
    key: card.id,
    className: "eligible-card-lockout",
    message: `We couldn’t verify your ${card.name === 'Platinum Card' ? 'Platinum Card®' : card.name} after 3 attempts. Verification for this Card is locked for 24 hours. You can add another eligible Card below.`,
    onDismiss: () => setLockoutNotificationCardId(null)
  })), /*#__PURE__*/React.createElement("div", {
    className: "eligible-card-list"
  }, cards.map((card, imageIndex) => {
    const isEnrolled = enrolledCards.includes(card.id);
    return /*#__PURE__*/React.createElement("div", {
      key: card.id,
      className: "eligible-card-row"
    }, /*#__PURE__*/React.createElement("img", {
      src: cardImageAt(imageIndex),
      alt: "",
      className: "pcard-img"
    }), /*#__PURE__*/React.createElement("div", {
      className: "pcard-details"
    }, /*#__PURE__*/React.createElement("span", {
      className: "pcard-name"
    }, card.name), /*#__PURE__*/React.createElement("span", {
      className: "pcard-mask"
    }, card.mask)), isCardVerificationLocked(card.id) ? /*#__PURE__*/React.createElement("span", {
      className: "eligible-card-locked",
      "aria-label": "Locked"
    }, /*#__PURE__*/React.createElement("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      width: "16",
      height: "16",
      viewBox: "0 0 12 16",
      fill: "none",
      "aria-hidden": "true"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M6 0C7.62453 0 8.95204 1.29827 8.99902 2.91211L9 5.33105H11C11.5304 5.33105 11.9675 5.74943 11.998 6.27246L12 14.333C12 14.8633 11.5816 15.3014 11.0586 15.332L1 15.333C0.469655 15.333 0.0325183 14.9146 0.00195312 14.3916L0 6.33105C0 5.80073 0.4184 5.36262 0.941406 5.33203L3 5.33105V3.00098C3 1.34635 4.34587 9.70065e-08 6 0ZM1 14.333H11V6.33301H9V6.33398H3V6.33301H1V14.333ZM6 8.33301C6.55216 8.33301 6.9998 8.78086 7 9.33301C7 9.67907 6.82207 9.98101 6.55469 10.1602L6.5 10.1943V11.833C6.5 12.1091 6.2762 12.333 6 12.333C5.74011 12.333 5.52627 12.1353 5.50195 11.8818L5.5 11.833V10.1943C5.20244 10.0211 5 9.70216 5 9.33301C5.0002 8.78086 5.44784 8.33301 6 8.33301ZM6 1C4.9223 1 4.04063 1.85754 4.00098 2.92676L4 5.33301H8V3.00195C7.99997 1.89804 7.10284 1 6 1Z",
      fill: "#737373"
    })), "Locked") : /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: `eligible-card-add${isEnrolled ? ' eligible-card-added' : ''}`,
      onClick: () => openCardVerification(card.id),
      disabled: isEnrolled
    }, isEnrolled ? /*#__PURE__*/React.createElement("svg", {
      className: "eligible-card-check",
      xmlns: "http://www.w3.org/2000/svg",
      width: "24",
      height: "24",
      viewBox: "0 0 24 24",
      fill: "none",
      "aria-label": "Card added"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M12 0C18.6274 0 24 5.37256 24 12C24 18.6274 18.6274 24 12 24C5.37259 24 0 18.6274 0 12C0 5.37259 5.37259 0 12 0ZM12 1.5C6.21027 1.5 1.5 6.21026 1.5 12C1.5 17.7897 6.21027 22.5 12 22.5C17.7897 22.5 22.5 17.7897 22.5 12C22.5 6.21027 17.7897 1.5 12 1.5ZM16.7197 8.21973C17.0126 7.92683 17.4874 7.92683 17.7803 8.21973C18.0548 8.49424 18.0721 8.92857 17.832 9.22314L17.7803 9.28027L10.7803 16.2803C10.4979 16.5626 10.0482 16.5719 9.75488 16.3135L9.70166 16.2617L6.20166 12.5117C5.91907 12.2089 5.93548 11.7343 6.23828 11.4517C6.52217 11.1867 6.95705 11.1848 7.24316 11.4351L7.29834 11.4883L10.2686 14.6709L16.7197 8.21973Z",
      fill: "#00804A"
    })) : /*#__PURE__*/React.createElement(React.Fragment, null, "Add ", /*#__PURE__*/React.createElement(IconChevronRight, {
      "aria-hidden": "true"
    }))));
  })), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "eligible-cards-continue",
    onClick: () => goToScreen('ready-to-pay')
  }, "Continue")) : null, activeScreen.key === 'ready-to-pay' ? /*#__PURE__*/React.createElement("div", {
    className: "ready-to-pay-shell",
    "aria-label": "You are ready to pay with Face Verification"
  }, /*#__PURE__*/React.createElement("div", {
    className: "ready-to-pay-success",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: "48",
    height: "48",
    viewBox: "0 0 48 48",
    fill: "none"
  }, /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M48 24C48 10.7451 37.2548 0 24 0C10.7452 0 0 10.7451 0 24C0 37.2548 10.7452 48 24 48C37.2548 48 48 37.2548 48 24ZM33.5533 16.3364C34.1424 15.8558 35.0115 15.8902 35.5607 16.4393C36.1098 16.9885 36.1442 17.8576 35.6636 18.4467L35.5607 18.5607L21.5607 32.5607L21.451 32.6602C20.8846 33.1257 20.0596 33.1115 19.5096 32.6267L19.4034 32.5235L12.4034 25.0235C11.8445 24.3007 11.9088 23.4333 12.4765 22.9034C13.0443 22.3735 13.914 22.3691 14.4863 22.8697L14.5966 22.9765L20.5372 29.3415L33.4393 16.4393L33.5533 16.3364Z",
    fill: "#00804A"
  }))), /*#__PURE__*/React.createElement("h2", null, "You\u2019re ready to pay with Face Verification."), /*#__PURE__*/React.createElement("p", null, "Your eligible American Express Card(s) are ready to use at participating checkouts."), /*#__PURE__*/React.createElement("section", {
    className: "ready-to-pay-section"
  }, /*#__PURE__*/React.createElement("h3", null, "Look for this at checkout"), /*#__PURE__*/React.createElement("div", {
    className: "ready-to-pay-card"
  }, /*#__PURE__*/React.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: "32",
    height: "32",
    viewBox: "0 0 32 32",
    fill: "none",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M22.9805 13.3335C22.9805 9.47901 19.8555 6.354 16.001 6.354C12.1465 6.354 9.02148 9.47901 9.02148 13.3335C9.02148 17.188 12.1465 20.313 16.001 20.313C19.8555 20.313 22.9805 17.188 22.9805 13.3335ZM25.0215 13.3335C25.0215 18.3156 20.983 22.354 16.001 22.354C11.0189 22.354 6.98047 18.3156 6.98047 13.3335C6.98047 8.35143 11.0189 4.31299 16.001 4.31299C20.983 4.31299 25.0215 8.35143 25.0215 13.3335Z",
    fill: "#00175A"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M3.54524 28.4201C11.8324 24.2766 20.1702 24.2766 28.4573 28.4201C28.9615 28.6721 29.1662 29.285 28.9144 29.7892C28.6623 30.2934 28.0494 30.4981 27.5452 30.2462C19.8326 26.3899 12.17 26.3899 4.45735 30.2462C3.95315 30.4981 3.34028 30.2934 3.08821 29.7892C2.83637 29.285 3.0411 28.6721 3.54524 28.4201Z",
    fill: "#00175A"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M4.3125 9.3335V2.6665C4.3125 2.10271 4.76922 1.646 5.33301 1.646H12C12.5636 1.64617 13.0205 2.10282 13.0205 2.6665C13.0205 3.23019 12.5636 3.68684 12 3.68701H6.35352V9.3335C6.35334 9.89714 5.89669 10.354 5.33301 10.354C4.76933 10.354 4.31268 9.89714 4.3125 9.3335Z",
    fill: "#00175A"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M25.6465 9.3335V3.68701H20.001C19.4372 3.68701 18.9805 3.23029 18.9805 2.6665C18.9805 2.10271 19.4372 1.646 20.001 1.646H26.668C27.2316 1.64617 27.6885 2.10282 27.6885 2.6665V9.3335C27.6883 9.89703 27.2315 10.3538 26.668 10.354C26.1043 10.354 25.6467 9.89714 25.6465 9.3335Z",
    fill: "#00175A"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M4.3125 17.3335C4.3125 16.7697 4.76922 16.313 5.33301 16.313C5.8968 16.313 6.35352 16.7697 6.35352 17.3335V22.979H12C12.5636 22.9792 13.0205 23.4368 13.0205 24.0005C13.0203 24.564 12.5635 25.0208 12 25.021H5.33301C4.76933 25.021 4.31268 24.5641 4.3125 24.0005V17.3335Z",
    fill: "#00175A"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M25.6465 17.3335C25.6465 16.7697 26.1042 16.313 26.668 16.313C27.2316 16.3132 27.6885 16.7698 27.6885 17.3335V24.0005C27.6883 24.564 27.2315 25.0208 26.668 25.021H20.001C19.4373 25.021 18.9806 24.5641 18.9805 24.0005C18.9805 23.4367 19.4372 22.979 20.001 22.979H25.6465V17.3335Z",
    fill: "#00175A"
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("strong", null, "Face Verification"), /*#__PURE__*/React.createElement("small", null, "AMERICAN EXPRESS"))), /*#__PURE__*/React.createElement("p", null, "At checkout, select ", /*#__PURE__*/React.createElement("strong", null, "Face Verification"), " from the payment options at checkout. Look for the symbol above.")), /*#__PURE__*/React.createElement("section", {
    className: "ready-to-pay-section"
  }, /*#__PURE__*/React.createElement("h3", null, "What to Expect"), /*#__PURE__*/React.createElement("div", {
    className: "ready-to-pay-step"
  }, /*#__PURE__*/React.createElement("svg", {
    className: "ready-to-pay-icon-b1",
    xmlns: "http://www.w3.org/2000/svg",
    width: "32",
    height: "32",
    viewBox: "0 0 32 32",
    fill: "none",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M22.9805 13.3335C22.9805 9.47901 19.8555 6.354 16.001 6.354C12.1465 6.354 9.02148 9.47901 9.02148 13.3335C9.02148 17.188 12.1465 20.313 16.001 20.313C19.8555 20.313 22.9805 17.188 22.9805 13.3335ZM25.0215 13.3335C25.0215 18.3156 20.983 22.354 16.001 22.354C11.0189 22.354 6.98047 18.3156 6.98047 13.3335C6.98047 8.35143 11.0189 4.31299 16.001 4.31299C20.983 4.31299 25.0215 8.35143 25.0215 13.3335Z",
    fill: "#00175A"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M3.54524 28.4201C11.8324 24.2766 20.1702 24.2766 28.4573 28.4201C28.9615 28.6721 29.1662 29.285 28.9144 29.7892C28.6623 30.2934 28.0494 30.4981 27.5452 30.2462C19.8326 26.3899 12.17 26.3899 4.45735 30.2462C3.95315 30.4981 3.34028 30.2934 3.08821 29.7892C2.83637 29.285 3.0411 28.6721 3.54524 28.4201Z",
    fill: "#00175A"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M4.3125 9.3335V2.6665C4.3125 2.10271 4.76922 1.646 5.33301 1.646H12C12.5636 1.64617 13.0205 2.10282 13.0205 2.6665C13.0205 3.23019 12.5636 3.68684 12 3.68701H6.35352V9.3335C6.35334 9.89714 5.89669 10.354 5.33301 10.354C4.76933 10.354 4.31268 9.89714 4.3125 9.3335Z",
    fill: "#00175A"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M25.6465 9.3335V3.68701H20.001C19.4372 3.68701 18.9805 3.23029 18.9805 2.6665C18.9805 2.10271 19.4372 1.646 20.001 1.646H26.668C27.2316 1.64617 27.6885 2.10282 27.6885 2.6665V9.3335C27.6883 9.89703 27.2315 10.3538 26.668 10.354C26.1043 10.354 25.6467 9.89714 25.6465 9.3335Z",
    fill: "#00175A"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M4.3125 17.3335C4.3125 16.7697 4.76922 16.313 5.33301 16.313C5.8968 16.313 6.35352 16.7697 6.35352 17.3335V22.979H12C12.5636 22.9792 13.0205 23.4368 13.0205 24.0005C13.0203 24.564 12.5635 25.0208 12 25.021H5.33301C4.76933 25.021 4.31268 24.5641 4.3125 24.0005V17.3335Z",
    fill: "#00175A"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M25.6465 17.3335C25.6465 16.7697 26.1042 16.313 26.668 16.313C27.2316 16.3132 27.6885 16.7698 27.6885 17.3335V24.0005C27.6883 24.564 27.2315 25.0208 26.668 25.021H20.001C19.4373 25.021 18.9806 24.5641 18.9805 24.0005C18.9805 23.4367 19.4372 22.979 20.001 22.979H25.6465V17.3335Z",
    fill: "#00175A"
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("strong", null, "Choose Face Verification"), /*#__PURE__*/React.createElement("span", null, "Look for it during checkout and select it."))), /*#__PURE__*/React.createElement("div", {
    className: "ready-to-pay-step"
  }, /*#__PURE__*/React.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: "32",
    height: "32",
    viewBox: "0 0 32 32",
    fill: "none",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M9.96513 5.16667L8.66653 8H4C2.8954 8 2 8.8954 2 10V26C2 27.1046 2.8954 28 4 28H28C29.1046 28 30 27.1046 30 26V10C30 8.8954 29.1046 8 28 8H23.3333L22.0347 5.16667C21.7089 4.45579 20.9986 4 20.2166 4H11.7833C11.0013 4 10.291 4.45573 9.96513 5.16667ZM3.99977 10H9.99977L11.7998 6H20.1998L21.9998 10H27.9998V26H3.99977V10ZM15.9998 12C19.3135 12 21.9998 14.6863 21.9998 18C21.9998 21.3137 19.3135 24 15.9998 24C12.686 24 9.99977 21.3137 9.99977 18C9.99977 14.6863 12.686 12 15.9998 12ZM11.9998 18C11.9998 15.7909 13.7906 14 15.9998 14C18.2089 14 19.9998 15.7909 19.9998 18C19.9998 20.2091 18.2089 22 15.9998 22C13.7906 22 11.9998 20.2091 11.9998 18Z",
    fill: "#00175A"
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("strong", null, "Look at the camera"), /*#__PURE__*/React.createElement("span", null, "Position yourself in front of the camera so we can verify it\u2019s you."))), /*#__PURE__*/React.createElement("div", {
    className: "ready-to-pay-step"
  }, /*#__PURE__*/React.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: "32",
    height: "24",
    viewBox: "0 0 32 24",
    fill: "none",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M32 2C32 0.895431 31.1046 0 30 0H6C4.89543 0 4 0.895431 4 2V4H2C0.895431 4 0 4.89543 0 6V22C0 23.1046 0.895431 24 2 24H26C27.1046 24 28 23.1046 28 22V20H30C31.1046 20 32 19.1046 32 18V2ZM2 10H26V22H2V10ZM10 12.6667C11.1046 12.6667 12 13.5621 12 14.6667V16.6667C12 17.7712 11.1046 18.6667 10 18.6667H6C4.89543 18.6667 4 17.7712 4 16.6667V14.6667C4 13.5621 4.89543 12.6667 6 12.6667H10ZM6 2H30V18H28V6C28 4.89543 27.1046 4 26 4H6V2ZM10 14.6667H6V16.6667H10V14.6667ZM2 6H26V8H2V6Z",
    fill: "#00175A"
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("strong", null, "Confirm your payment"), /*#__PURE__*/React.createElement("span", null, "Your default card will be selected. You can choose another eligible Card before paying.")))), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "ready-to-pay-done",
    onClick: () => goToScreen('face-verification-status')
  }, "Done")) : null, activeScreen.key === 'face-verification-status' ? /*#__PURE__*/React.createElement("div", {
    className: "face-verification-status-shell",
    "aria-label": "Face Verification status"
  }, showFaceUnenrollNotification ? /*#__PURE__*/React.createElement("div", {
    className: "face-verification-card-notification",
    role: "status"
  }, /*#__PURE__*/React.createElement("span", {
    className: "face-verification-notification-icon",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24"
  }, /*#__PURE__*/React.createElement("circle", {
    cx: "12",
    cy: "12",
    r: "12",
    fill: "currentColor"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m7 12.3 3 3 7-7",
    fill: "none",
    stroke: "#ffffff",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }))), /*#__PURE__*/React.createElement("p", null, "Face Verification and biometric data was deleted."), /*#__PURE__*/React.createElement("button", {
    type: "button",
    "aria-label": "Dismiss notification",
    onClick: () => setShowFaceUnenrollNotification(false)
  }, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  }))) : null, /*#__PURE__*/React.createElement("section", {
    className: "face-verification-status-card"
  }, /*#__PURE__*/React.createElement("h2", null, "Face Verification"), hasFaceVerificationCards ? /*#__PURE__*/React.createElement("p", {
    className: "face-verification-active"
  }, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  }), "Active ", /*#__PURE__*/React.createElement("span", {
    className: "face-verification-divider"
  }, "\xB7"), " Enrolled ", faceVerificationEnrolledDate || formatFaceVerificationDate()) : faceVerificationRemovedDate ? /*#__PURE__*/React.createElement("p", {
    className: "face-verification-active face-verification-inactive"
  }, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  }), "Inactive ", /*#__PURE__*/React.createElement("span", {
    className: "face-verification-divider"
  }, "\xB7"), " Removed on ", faceVerificationRemovedDate) : /*#__PURE__*/React.createElement("p", {
    className: "face-verification-active face-verification-inactive"
  }, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  }), "Inactive"), /*#__PURE__*/React.createElement("p", null, hasFaceVerificationCards ? 'Face Verification is ready to use at checkout.' : faceVerificationRemovedDate ? 'Your biometric data was deleted and you can no longer use Face Verification.' : 'Enroll an eligible Card to use Face Verification at checkout.')), !hasFaceVerificationCards ? /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "face-verification-unenroll-card face-verification-reenroll-card",
    onClick: beginFaceVerificationReenrollment
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("strong", null, faceVerificationRemovedDate ? 'Re-enroll Face Verification' : 'Enroll Face Verification'), /*#__PURE__*/React.createElement("small", null, "Add your biometric profile and eligible card(s).")), /*#__PURE__*/React.createElement(IconChevronRight, {
    "aria-hidden": "true"
  })) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("section", {
    className: "face-verification-status-card face-verification-cards-card"
  }, /*#__PURE__*/React.createElement("div", {
    className: "face-verification-card-heading"
  }, /*#__PURE__*/React.createElement("h2", null, "Cards"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "face-verification-view-all",
    onClick: () => goToScreen('face-verification-cards')
  }, "View all ", /*#__PURE__*/React.createElement(IconChevronRight, {
    "aria-hidden": "true"
  }))), enrolledCards.map((cardId, imageIndex) => {
    const card = cards.find(item => item.id === cardId);
    if (!card) return null;
    return /*#__PURE__*/React.createElement("div", {
      key: card.id,
      className: "face-verification-enrolled-card"
    }, /*#__PURE__*/React.createElement("img", {
      src: cardImageAt(imageIndex),
      alt: "",
      className: "face-verification-card-image"
    }), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("strong", null, card.name), /*#__PURE__*/React.createElement("span", null, card.mask), defaultPaymentCard === card.id ? /*#__PURE__*/React.createElement("small", null, "Default") : null));
  }), /*#__PURE__*/React.createElement("p", {
    className: "face-verification-card-copy"
  }, "Default card is preselected at checkout. You can still choose another card when you pay.")), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "face-verification-unenroll-card",
    onClick: openUnenrollModalForCurrentScreen
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("strong", null, "Unenroll"), /*#__PURE__*/React.createElement("small", null, "Unenroll biometric profile and eligible card(s) provisioned.")), /*#__PURE__*/React.createElement(IconChevronRight, {
    "aria-hidden": "true"
  })))) : null, activeScreen.key === 'face-verification-cards' ? (() => {
    const readyCards = cards.filter(card => enrolledCards.includes(card.id));
    const availableCards = cards.filter(card => !enrolledCards.includes(card.id));
    const recentlyAddedCard = cards.find(card => card.id === recentlyAddedCardId);
    return /*#__PURE__*/React.createElement("div", {
      className: "face-verification-status-shell face-verification-cards-shell",
      "aria-label": "Face Verification cards"
    }, /*#__PURE__*/React.createElement("section", {
      className: "face-verification-status-card face-verification-cards-overview-card"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "face-verification-back-button",
      onClick: () => goToScreen('face-verification-status')
    }, /*#__PURE__*/React.createElement(IconChevronRight, {
      "aria-hidden": "true"
    }), " Back"), /*#__PURE__*/React.createElement("h2", null, "Cards"), /*#__PURE__*/React.createElement("p", null, recentlyAddedCard ? 'These cards are ready to use with Face Verification.' : 'This card is ready to use with Face Verification.'), recentlyAddedCard ? /*#__PURE__*/React.createElement("div", {
      className: "face-verification-card-notification",
      role: "status"
    }, /*#__PURE__*/React.createElement("span", {
      className: "face-verification-notification-icon",
      "aria-hidden": "true"
    }, /*#__PURE__*/React.createElement("svg", {
      viewBox: "0 0 24 24"
    }, /*#__PURE__*/React.createElement("circle", {
      cx: "12",
      cy: "12",
      r: "12",
      fill: "currentColor"
    }), /*#__PURE__*/React.createElement("path", {
      d: "m7 12.3 3 3 7-7",
      fill: "none",
      stroke: "#ffffff",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }))), /*#__PURE__*/React.createElement("p", null, /*#__PURE__*/React.createElement("strong", null, recentlyAddedCard.name), " ", recentlyAddedCard.mask.replace('****', '••••'), " added."), /*#__PURE__*/React.createElement("button", {
      type: "button",
      "aria-label": "Dismiss notification",
      onClick: () => setRecentlyAddedCardId(null)
    }, /*#__PURE__*/React.createElement("span", {
      "aria-hidden": "true"
    }))) : null, defaultUpdateErrorCardId ? /*#__PURE__*/React.createElement("div", {
      className: "face-verification-card-notification face-verification-card-notification-error",
      role: "alert"
    }, /*#__PURE__*/React.createElement("span", {
      className: "face-verification-notification-icon",
      "aria-hidden": "true"
    }, /*#__PURE__*/React.createElement("svg", {
      viewBox: "0 0 24 24"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M12 2 22 20H2L12 2Z",
      fill: "currentColor"
    }), /*#__PURE__*/React.createElement("path", {
      d: "M12 8v6",
      stroke: "#ffffff",
      strokeWidth: "2",
      strokeLinecap: "round"
    }), /*#__PURE__*/React.createElement("circle", {
      cx: "12",
      cy: "17",
      r: "1.2",
      fill: "#ffffff"
    }))), /*#__PURE__*/React.createElement("p", null, "Your default card couldn\u2019t be updated. Try again."), /*#__PURE__*/React.createElement("button", {
      type: "button",
      "aria-label": "Dismiss notification",
      onClick: () => setDefaultUpdateErrorCardId(null)
    }, /*#__PURE__*/React.createElement("span", {
      "aria-hidden": "true"
    }))) : null, /*#__PURE__*/React.createElement("div", {
      className: "face-verification-card-list"
    }, readyCards.map((card, imageIndex) => /*#__PURE__*/React.createElement("div", {
      key: card.id,
      className: "face-verification-card-row"
    }, /*#__PURE__*/React.createElement("img", {
      src: cardImageAt(imageIndex),
      alt: "",
      className: "face-verification-card-image"
    }), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("strong", null, card.name), /*#__PURE__*/React.createElement("span", null, card.mask), defaultPaymentCard === card.id ? /*#__PURE__*/React.createElement("small", null, "Default") : /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "face-verification-make-default",
      onClick: () => updateDefaultPaymentCard(card.id)
    }, "Make Default")))))), availableCards.length > 0 ? /*#__PURE__*/React.createElement("section", {
      className: "face-verification-status-card face-verification-available-card"
    }, /*#__PURE__*/React.createElement("h3", null, "Available to Add"), /*#__PURE__*/React.createElement("p", null, "Add another eligible card to use with Face Verification."), /*#__PURE__*/React.createElement("div", {
      className: "face-verification-card-list"
    }, availableCards.map((card, imageIndex) => /*#__PURE__*/React.createElement(React.Fragment, {
      key: card.id
    }, isCardVerificationLocked(card.id) ? /*#__PURE__*/React.createElement(VerificationLockNotification, {
      autoDismiss: false,
      className: "face-verification-lock-message",
      message: "Verification for this Card is locked for 24 hours."
    }) : null, /*#__PURE__*/React.createElement("div", {
      className: "face-verification-card-row face-verification-available-row"
    }, /*#__PURE__*/React.createElement("img", {
      src: cardImageAt(imageIndex),
      alt: "",
      className: "face-verification-card-image"
    }), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("strong", null, card.name), /*#__PURE__*/React.createElement("span", null, card.mask)), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "face-verification-card-add",
      onClick: () => {
        setEnrollOriginScreen('face-verification-cards');
        openCardVerification(card.id);
      },
      disabled: isCardVerificationLocked(card.id)
    }, isCardVerificationLocked(card.id) ? /*#__PURE__*/React.createElement("span", {
      className: "face-verification-card-locked"
    }, /*#__PURE__*/React.createElement("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      width: "16",
      height: "16",
      viewBox: "0 0 12 16",
      fill: "none",
      "aria-hidden": "true"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M6 0C7.62453 0 8.95204 1.29827 8.99902 2.91211L9 5.33105H11C11.5304 5.33105 11.9675 5.74943 11.998 6.27246L12 14.333C12 14.8633 11.5816 15.3014 11.0586 15.332L1 15.333C0.469655 15.333 0.0325183 14.9146 0.00195312 14.3916L0 6.33105C0 5.80073 0.4184 5.36262 0.941406 5.33203L3 5.33105V3.00098C3 1.34635 4.34587 9.70065e-08 6 0ZM1 14.333H11V6.33301H9V6.33398H3V6.33301H1V14.333ZM6 8.33301C6.55216 8.33301 6.9998 8.78086 7 9.33301C7 9.67907 6.82207 9.98101 6.55469 10.1602L6.5 10.1943V11.833C6.5 12.1091 6.2762 12.333 6 12.333C5.74011 12.333 5.52627 12.1353 5.50195 11.8818L5.5 11.833V10.1943C5.20244 10.0211 5 9.70216 5 9.33301C5.0002 8.78086 5.44784 8.33301 6 8.33301ZM6 1C4.9223 1 4.04063 1.85754 4.00098 2.92676L4 5.33301H8V3.00195C7.99997 1.89804 7.10284 1 6 1Z",
      fill: "#737373"
    })), "Locked") : /*#__PURE__*/React.createElement(React.Fragment, null, "Add ", /*#__PURE__*/React.createElement(IconChevronRight, {
      "aria-hidden": "true"
    })))))))) : null);
  })() : null, activeScreen.key === 'payment-selection-multi' ? /*#__PURE__*/React.createElement("div", {
    className: "payment-shell",
    "aria-label": "Payment method selection multiple cards screen"
  }, enrolledCards.some(id => cards.some(c => c.id === id)) && /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "unenroll-top-link",
    onClick: openUnenrollModalForCurrentScreen
  }, "Unenroll"), /*#__PURE__*/React.createElement("div", {
    className: "pscreen-header"
  }, /*#__PURE__*/React.createElement("h2", {
    className: "payment-title"
  }, "Returning card member, multiple cards, default card set.")), /*#__PURE__*/React.createElement("div", {
    className: "pcard-list"
  }, cards.map((card, imageIndex) => {
    const isEnrolled = enrolledCards.includes(card.id);
    return /*#__PURE__*/React.createElement("div", {
      key: card.id,
      className: `pcard-row ${!isEnrolled && selectedPaymentCard === card.id ? 'pcard-row-selected' : ''} ${isEnrolled ? 'pcard-row-enrolled' : ''}`,
      role: "button",
      tabIndex: 0,
      onClick: () => !isEnrolled && setSelectedPaymentCard(card.id),
      onKeyDown: e => e.key === 'Enter' && !isEnrolled && setSelectedPaymentCard(card.id)
    }, /*#__PURE__*/React.createElement("span", {
      className: `pcard-radio ${!isEnrolled && selectedPaymentCard === card.id ? 'pcard-radio-on' : ''}`,
      "aria-hidden": "true"
    }), /*#__PURE__*/React.createElement("img", {
      src: cardImageAt(imageIndex),
      alt: "",
      className: "pcard-img"
    }), /*#__PURE__*/React.createElement("div", {
      className: "pcard-details"
    }, /*#__PURE__*/React.createElement("span", {
      className: "pcard-name"
    }, card.name), /*#__PURE__*/React.createElement("span", {
      className: "pcard-mask"
    }, card.mask), isEnrolled ? /*#__PURE__*/React.createElement("span", {
      className: "pcard-enrolled-badge"
    }, "Enrolled") : defaultPaymentCard === card.id ? /*#__PURE__*/React.createElement("span", {
      className: "pcard-default-badge"
    }, "Default") : /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "pcard-set-default",
      onClick: event => {
        event.stopPropagation();
        setDefaultPaymentCard(card.id);
        setSelectedPaymentCard(card.id);
      }
    }, "Set as default")));
  })), /*#__PURE__*/React.createElement("div", {
    className: "payment-button-group"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "payment-primary-button",
    onClick: openConsentModal,
    disabled: !selectedPaymentCard || enrolledCards.includes(selectedPaymentCard)
  }, selectedPaymentCard && enrolledCards.includes(selectedPaymentCard) ? 'Already enrolled' : 'Activate'), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "payment-secondary-button",
    onClick: goToPaymentSelection
  }, "Back"))) : null, activeScreen.key === 'payment-no-eligible' ? /*#__PURE__*/React.createElement("div", {
    className: "payment-shell",
    "aria-label": "No eligible payment methods screen"
  }, /*#__PURE__*/React.createElement("h2", {
    className: "payment-title"
  }, "No eligible payment methods"), /*#__PURE__*/React.createElement("p", {
    className: "payment-subtitle"
  }, "No eligible cards."), /*#__PURE__*/React.createElement("div", {
    className: "result-icon result-icon-failed",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("span", {
    className: "result-cross"
  })), /*#__PURE__*/React.createElement("div", {
    className: "payment-button-group"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "payment-primary-button",
    onClick: goToPaymentSelection
  }, "Back to selection"))) : null, activeScreen.key === 'adding-card-processing' ? /*#__PURE__*/React.createElement("div", {
    className: "adding-card-processing-shell",
    "aria-label": "Adding your card"
  }, /*#__PURE__*/React.createElement("div", {
    className: "adding-card-spinner",
    "aria-hidden": "true"
  }), /*#__PURE__*/React.createElement("h2", null, "Adding your card..."), /*#__PURE__*/React.createElement("div", {
    className: "adding-card-status-list"
  }, /*#__PURE__*/React.createElement("div", {
    className: "adding-card-status-row"
  }, /*#__PURE__*/React.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: "32",
    height: "32",
    viewBox: "0 0 32 32",
    fill: "none",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M22.9805 13.3335C22.9805 9.47901 19.8555 6.354 16.001 6.354C12.1465 6.354 9.02148 9.47901 9.02148 13.3335C9.02148 17.188 12.1465 20.313 16.001 20.313C19.8555 20.313 22.9805 17.188 22.9805 13.3335ZM25.0215 13.3335C25.0215 18.3156 20.983 22.354 16.001 22.354C11.0189 22.354 6.98047 18.3156 6.98047 13.3335C6.98047 8.35143 11.0189 4.31299 16.001 4.31299C20.983 4.31299 25.0215 8.35143 25.0215 13.3335Z",
    fill: "#006FCF"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M3.54524 28.4201C11.8324 24.2766 20.1702 24.2766 28.4573 28.4201C28.9615 28.6721 29.1662 29.285 28.9144 29.7892C28.6623 30.2934 28.0494 30.4981 27.5452 30.2462C19.8326 26.3899 12.17 26.3899 4.45735 30.2462C3.95315 30.4981 3.34028 30.2934 3.08821 29.7892C2.83637 29.285 3.0411 28.6721 3.54524 28.4201Z",
    fill: "#006FCF"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M4.3125 9.3335V2.6665C4.3125 2.10271 4.76922 1.646 5.33301 1.646H12C12.5636 1.64617 13.0205 2.10282 13.0205 2.6665C13.0205 3.23019 12.5636 3.68684 12 3.68701H6.35352V9.3335C6.35334 9.89714 5.89669 10.354 5.33301 10.354C4.76933 10.354 4.31268 9.89714 4.3125 9.3335Z",
    fill: "#006FCF"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M25.6465 9.3335V3.68701H20.001C19.4372 3.68701 18.9805 3.23029 18.9805 2.6665C18.9805 2.10271 19.4372 1.646 20.001 1.646H26.668C27.2316 1.64617 27.6885 2.10282 27.6885 2.6665V9.3335C27.6883 9.89703 27.2315 10.3538 26.668 10.354C26.1043 10.354 25.6467 9.89714 25.6465 9.3335Z",
    fill: "#006FCF"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M4.3125 17.3335C4.3125 16.7697 4.76922 16.313 5.33301 16.313C5.8968 16.313 6.35352 16.7697 6.35352 17.3335V22.979H12C12.5636 22.9792 13.0205 23.4368 13.0205 24.0005C13.0203 24.564 12.5635 25.0208 12 25.021H5.33301C4.76933 25.021 4.31268 24.5641 4.3125 24.0005V17.3335Z",
    fill: "#006FCF"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M25.6465 17.3335C25.6465 16.7697 26.1042 16.313 26.668 16.313C27.2316 16.3132 27.6885 16.7698 27.6885 17.3335V24.0005C27.6883 24.564 27.2315 25.0208 26.668 25.021H20.001C19.4373 25.021 18.9806 24.5641 18.9805 24.0005C18.9805 23.4367 19.4372 22.979 20.001 22.979H25.6465V17.3335Z",
    fill: "#006FCF"
  })), /*#__PURE__*/React.createElement("span", null, "Preparing for face verification")), /*#__PURE__*/React.createElement("div", {
    className: "adding-card-status-row"
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("rect", {
    x: "5",
    y: "10",
    width: "14",
    height: "11",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.7"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M8 10V7a4 4 0 0 1 8 0v3M12 14v4",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.7",
    strokeLinecap: "round"
  })), /*#__PURE__*/React.createElement("span", null, "Setting up payment credentials")), /*#__PURE__*/React.createElement("div", {
    className: "adding-card-status-row"
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M12 2.5 19 6v6.5c0 4.5-2.7 7.8-7 10-4.3-2.2-7-5.5-7-10V6l7-3.5Z",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.7",
    strokeLinejoin: "round"
  })), /*#__PURE__*/React.createElement("span", null, "Securing card details")), /*#__PURE__*/React.createElement("div", {
    className: "adding-card-status-row"
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 24 24",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("circle", {
    cx: "12",
    cy: "12",
    r: "9",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.7"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m8 12 2.6 2.6L16.5 9",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.7",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })), /*#__PURE__*/React.createElement("span", null, "Ready for face verification")))) : null, activeScreen.key === 'payment-processing' ? /*#__PURE__*/React.createElement("div", {
    className: "processing-shell",
    "aria-label": "Payment enrollment screen"
  }, /*#__PURE__*/React.createElement("h2", {
    className: "processing-title"
  }, "Processing enrollment..."), /*#__PURE__*/React.createElement("div", {
    className: "processing-spinner",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("span", null), /*#__PURE__*/React.createElement("span", null), /*#__PURE__*/React.createElement("span", null), /*#__PURE__*/React.createElement("span", null), /*#__PURE__*/React.createElement("span", null), /*#__PURE__*/React.createElement("span", null), /*#__PURE__*/React.createElement("span", null), /*#__PURE__*/React.createElement("span", null)), /*#__PURE__*/React.createElement("p", {
    className: "processing-copy"
  }, /*#__PURE__*/React.createElement("br", null)), /*#__PURE__*/React.createElement("div", {
    className: "processing-button-group"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "processing-failed-button",
    onClick: goToPaymentDeclined
  }, "Enrollment declined"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "processing-success-button",
    onClick: goToPaymentApproved
  }, "Enrollment approved"))) : null, activeScreen.key === 'payment-approved' ? (() => {
    const card = cards.find(c => c.id === selectedPaymentCard);
    return /*#__PURE__*/React.createElement("div", {
      className: "result-shell result-shell-success",
      "aria-label": "Payment approved result"
    }, /*#__PURE__*/React.createElement("h2", {
      className: "result-title"
    }, "Enrollment approved"), /*#__PURE__*/React.createElement("div", {
      className: "result-icon result-icon-success",
      "aria-hidden": "true"
    }, /*#__PURE__*/React.createElement("span", {
      className: "result-check"
    })), card && /*#__PURE__*/React.createElement("div", {
      className: "result-card-detail"
    }, /*#__PURE__*/React.createElement("img", {
      src: cardImageAt(0),
      alt: "",
      className: "pcard-img"
    }), /*#__PURE__*/React.createElement("div", {
      className: "pcard-details"
    }, /*#__PURE__*/React.createElement("span", {
      className: "pcard-name"
    }, card.name), /*#__PURE__*/React.createElement("span", {
      className: "pcard-mask"
    }, card.mask))), /*#__PURE__*/React.createElement("p", {
      className: "result-copy"
    }, "Enrollment complete!"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "result-button",
      onClick: () => goToScreen(enrollOriginScreen)
    }, "Ok"));
  })() : null, activeScreen.key === 'payment-declined' ? (() => {
    const card = cards.find(c => c.id === selectedPaymentCard);
    return /*#__PURE__*/React.createElement("div", {
      className: "result-shell result-shell-failed",
      "aria-label": "Payment declined result"
    }, /*#__PURE__*/React.createElement("h2", {
      className: "result-title"
    }, "Enrollment declined"), /*#__PURE__*/React.createElement("div", {
      className: "result-icon result-icon-failed",
      "aria-hidden": "true"
    }, /*#__PURE__*/React.createElement("span", {
      className: "result-cross"
    })), card && /*#__PURE__*/React.createElement("div", {
      className: "result-card-detail"
    }, /*#__PURE__*/React.createElement("img", {
      src: cardImageAt(0),
      alt: "",
      className: "pcard-img"
    }), /*#__PURE__*/React.createElement("div", {
      className: "pcard-details"
    }, /*#__PURE__*/React.createElement("span", {
      className: "pcard-name"
    }, card.name), /*#__PURE__*/React.createElement("span", {
      className: "pcard-mask"
    }, card.mask))), /*#__PURE__*/React.createElement("p", {
      className: "result-copy"
    }, "Enrollment declined."), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "result-button",
      onClick: () => goToScreen(enrollOriginScreen)
    }, "Ok"));
  })() : null, activeScreen.key === 'capture-success' ? /*#__PURE__*/React.createElement("div", {
    className: "capture-success-shell",
    "aria-label": "Face successfully enrolled"
  }, /*#__PURE__*/React.createElement("div", {
    className: "capture-success-icon",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: "62",
    height: "62",
    viewBox: "0 0 62 62",
    fill: "none"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M44.5234 25.8332C44.5234 18.3651 38.4687 12.3104 31.0007 12.3104C23.5326 12.3104 17.4779 18.3651 17.4779 25.8332C17.4779 33.3013 23.5326 39.356 31.0007 39.356C38.4687 39.356 44.5234 33.3013 44.5234 25.8332ZM48.4779 25.8332C48.4779 35.4859 40.6534 43.3104 31.0007 43.3104C21.3479 43.3104 13.5234 35.4859 13.5234 25.8332C13.5234 16.1804 21.3479 8.35596 31.0007 8.35596C40.6534 8.35596 48.4779 16.1804 48.4779 25.8332Z",
    fill: "#016FD0"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M6.86768 55.0644C22.924 47.0365 39.0786 47.0365 55.1349 55.0644C56.1117 55.5528 56.5083 56.7402 56.0204 57.7171C55.532 58.6939 54.3446 59.0905 53.3677 58.6026C38.4245 51.131 23.5781 51.131 8.63489 58.6026C7.65801 59.0905 6.47057 58.6939 5.98218 57.7171C5.49425 56.7402 5.89091 55.5528 6.86768 55.0644Z",
    fill: "#016FD0"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M8.35547 18.084V5.16669C8.35547 4.07434 9.24036 3.18945 10.3327 3.18945H23.25C24.3421 3.18979 25.2272 4.07455 25.2272 5.16669C25.2272 6.25882 24.3421 7.14358 23.25 7.14392H12.3099V18.084C12.3096 19.176 11.4248 20.0612 10.3327 20.0612C9.24057 20.0612 8.35581 19.176 8.35547 18.084Z",
    fill: "#016FD0"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M49.6888 18.084V7.14392H38.7507C37.6583 7.14392 36.7734 6.25903 36.7734 5.16669C36.7734 4.07434 37.6583 3.18945 38.7507 3.18945H51.668C52.76 3.18979 53.6452 4.07455 53.6452 5.16669V18.084C53.6449 19.1758 52.7598 20.0609 51.668 20.0612C50.5758 20.0612 49.6892 19.176 49.6888 18.084Z",
    fill: "#016FD0"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M8.35547 33.5832C8.35547 32.4908 9.24036 31.606 10.3327 31.606C11.425 31.606 12.3099 32.4908 12.3099 33.5832V44.5214H23.25C24.3421 44.5217 25.2272 45.4084 25.2272 46.5005C25.2269 47.5923 24.3418 48.4774 23.25 48.4777H10.3327C9.24057 48.4777 8.35581 47.5925 8.35547 46.5005V33.5832Z",
    fill: "#016FD0"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M49.6888 33.5832C49.6888 32.4908 50.5756 31.606 51.668 31.606C52.76 31.6063 53.6452 32.4911 53.6452 33.5832V46.5005C53.6449 47.5923 52.7598 48.4774 51.668 48.4777H38.7507C37.6585 48.4777 36.7738 47.592 36.7734 46.5005C36.7734 45.4081 37.6583 44.5214 38.7507 44.5214H49.6888V33.5832Z",
    fill: "#016FD0"
  }))), /*#__PURE__*/React.createElement("h2", {
    className: "capture-success-title"
  }, "Face successfully enrolled"), /*#__PURE__*/React.createElement("p", {
    className: "capture-success-copy"
  }, "Your face has been securely enrolled. Next, we\u2019ll confirm the Card you\u2019ll use with Face Verification."), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "capture-success-button",
    onClick: continueAfterFaceCapture
  }, "Continue")) : null)));
}